import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/Button';
import { WorkOrder, User, Comment, Environment, Status, Rating, ChecklistItem, WorkOrderEvent } from '../types';
import { getStatusColor, getStatusLabel, MOCK_USERS, calculateEndDate, calculateBusinessDays } from '../services/mockData';
import { ArrowLeft, Send, Paperclip, CheckSquare, Clock, MapPin, User as UserIcon, Calendar, Camera, XCircle, CheckCircle, AlertTriangle, Star, X, Pencil, Trash, Save, Clipboard, ChevronLeft, ChevronRight, ChevronDown, Plus, MinusCircle, BarChart3, Lock, UserCheck, Sparkles, MessageCircle, PackageCheck, Loader2, Mic, Square, FileText, File as FileIcon, Play, Pause, ListPlus, AlertCircle, Mail, Phone, LayoutGrid } from 'lucide-react';
import { format, isValid, addMonths, endOfMonth, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns';
import { db, supabase } from '../services/db';
import { OccurrenceFormModal } from './OccurrenceFormModal';
import { EventMediaModal } from './EventMediaModal';
import { OSChecklist } from './OSChecklist';
import { ConfirmModal } from './ui/ConfirmModal';
import { calculateEnvironmentSchedule, calculateOrderEndDate } from '../utils/scheduleCalculator';

interface WorkOrderDetailsProps {
  order: WorkOrder;
  currentUser: User;
  onBack: () => void;
  onUpdateOrder: (updatedOrder: WorkOrder, alertType?: 'ORDER_UPDATE' | 'CHAT_MESSAGE', alertMsg?: string, alertTitle?: string) => void;
  onDeleteOrder: (id: string) => void;
  allTechnicians: User[];
  initialTab?: 'DETAILS' | 'CHAT' | 'ENVIRONMENTS' | 'CHECKLIST';
}

// --- Custom Date Picker Component ---
const DatePicker = ({ label, value, onChange }: { label: string, value: string, onChange: (date: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value + 'T00:00:00') : new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(addMonths(currentMonth, -1));

  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const startOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  };

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth))
  });

  const handleDayClick = (day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const monthName = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(currentMonth);

  return (
    <div className="space-y-1 relative" ref={containerRef}>
      <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
        <Calendar size={12} /> {label}
      </label>
      <div
        onClick={toggleOpen}
        style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', color: 'var(--sf-white)', borderRadius: 8 }}
        className="w-full p-3 flex justify-between items-center cursor-pointer transition-colors"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value ? format(new Date(value + 'T00:00:00'), 'dd/MM/yyyy') : 'Selecione uma data'}
        </span>
        <Calendar size={20} className="text-gray-400" />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-2 rounded-xl p-4 w-[300px] animate-fade-in" style={{ background: 'var(--navy3)', border: '1px solid rgba(255,255,255,.12)', boxShadow: '0 12px 40px rgba(0,0,0,.6)' }}>
          <div className="flex justify-between items-center mb-4">
            <button type="button" onClick={prevMonth} className="p-1 rounded-full" style={{ color: 'var(--muted)' }}><ChevronLeft size={20} /></button>
            <span className="font-bold capitalize" style={{ color: 'var(--sf-white)' }}>{monthName}</span>
            <button type="button" onClick={nextMonth} className="p-1 rounded-full" style={{ color: 'var(--muted)' }}><ChevronRight size={20} /></button>
          </div>
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => {
              const isSelected = value ? isSameDay(day, new Date(value + 'T00:00:00')) : false;
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTodayDate = isToday(day);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  style={isSelected ? { background: 'var(--blue)', color: '#fff', fontWeight: 700 } : isTodayDate ? { border: '1px solid var(--blue)', color: 'var(--cyan)', fontWeight: 700 } : { color: isCurrentMonth ? 'var(--sf-white)' : 'var(--hint)' }}
                  className="h-8 w-8 rounded-full text-sm flex items-center justify-center transition-colors hover:opacity-80"
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t flex justify-center">
            <button type="button" onClick={() => { setCurrentMonth(new Date()); }} className="text-xs text-brand-600 font-bold hover:underline">
              Ir para Hoje
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Progress Bar Component ---
const ProgressBar = ({ items }: { items: ChecklistItem[] }) => {
  if (!items || items.length === 0) return null;
  const checkedCount = items.filter(i => i.checked).length;
  const progress = Math.round((checkedCount / items.length) * 100);

  let colorClass = 'bg-brand-600';
  if (progress === 100) colorClass = 'bg-green-500';
  else if (progress > 0) colorClass = 'bg-brand-500';
  else colorClass = 'bg-gray-300';

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
          <BarChart3 size={12} /> Progresso
        </span>
        <span className={`text-xs font-bold ${progress === 100 ? 'text-green-600' : 'text-gray-600'}`}>
          {progress}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-200">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ease-out ${colorClass}`}
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    </div>
  );
};

class ChecklistErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state: { hasError: boolean };
  props: { children: React.ReactNode };

  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('Erro no painel de materiais:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 text-center py-12">
          <h3 className="font-bold text-base mb-2">Erro ao carregar o painel</h3>
          <p className="text-sm text-gray-400">Ocorreu um erro inesperado ao carregar a aba de Materiais e Ferramentas.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export const WorkOrderDetails: React.FC<WorkOrderDetailsProps> = ({ order, currentUser, onBack, onUpdateOrder, onDeleteOrder, allTechnicians, initialTab }) => {
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'danger' | 'primary';
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<'DETAILS' | 'CHAT' | 'ENVIRONMENTS' | 'OCCURRENCES' | 'CHECKLIST'>(
    currentUser.role === 'CLIENT'
      ? (order.type === 'ASSEMBLY' ? 'ENVIRONMENTS' : 'OCCURRENCES')
      : (initialTab ?? 'DETAILS')
  );
  const [showQuickActions, setShowQuickActions] = useState(false);
  
  // --- Events / Occurrences ---
  const [events, setEvents] = useState<any[]>([]);
  const [showOccurrenceModal, setShowOccurrenceModal] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventMediaModalData, setEventMediaModalData] = useState<{ attachments: any[], initialIndex?: number } | null>(null);
  const [editEventData, setEditEventData] = useState<any>(null);

  const loadEvents = async () => {
    setIsLoadingEvents(true);
    const data = await db.getWorkOrderEvents(order.id);
    setEvents(data);
    setIsLoadingEvents(false);
  };

  useEffect(() => {
    loadEvents();
  }, [order.id]);

  const [newMessage, setNewMessage] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  // Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signaturePassword, setSignaturePassword] = useState('');
  const [signatureError, setSignatureError] = useState('');

  // Rating Prompt Modal
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const ratingRef = useRef<HTMLDivElement>(null);

  // Admin Editing State
  const [isEditing, setIsEditing] = useState(false);

  // Edit Form State
  const [editForm, setEditForm] = useState({
    clientName: order.clientName,
    clientPhone: order.clientPhone,
    address: order.address,
    cep: order.cep,
    referencePoint: order.referencePoint,
    description: order.description,
    suggestedDate: order.suggestedDate || '',
    suggestedTime: order.suggestedTime || '',
    assistanceChecklist: order.assistanceChecklist || [],
    assistanceOriginReason: order.assistanceOriginReason || '' as WorkOrder['assistanceOriginReason'] | '',
    environments: order.environments || []
  });

  // --- Admin/Superadmin Environment Status Edit ---
  const [editingEnvStatusId, setEditingEnvStatusId] = useState<string | null>(null);
  const [newEnvStatusValue, setNewEnvStatusValue] = useState<'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | ''>('');
  const [envStatusUpdateError, setEnvStatusUpdateError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  // Click outside and Escape key listeners for status popover
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingEnvStatusId(null);
        setNewEnvStatusValue('');
        setEnvStatusUpdateError(null);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (editingEnvStatusId && !target.closest('.status-popover-container')) {
        setEditingEnvStatusId(null);
        setNewEnvStatusValue('');
        setEnvStatusUpdateError(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingEnvStatusId]);

  const [envNewItemTexts, setEnvNewItemTexts] = useState<{ [key: number]: string }>({});
  const [newItemText, setNewItemText] = useState('');

  // --- New Environment Builder State (for Admins/Assistants) ---
  const [newEnvName, setNewEnvName] = useState('');
  const [newEnvDays, setNewEnvDays] = useState(1);
  const [newEnvValueBrl, setNewEnvValueBrl] = useState<string>(''); 
  const [newEnvStartDate, setNewEnvStartDate] = useState('');
  const [newEnvTechnicianId, setNewEnvTechnicianId] = useState('');
  const [newEnvSubItems, setNewEnvSubItems] = useState<string[]>([]);
  const [newEnvSubItemInput, setNewEnvSubItemInput] = useState('');
  const [newEnvProjectFile, setNewEnvProjectFile] = useState<{ url: string; name: string } | null>(null);
  const newEnvProjectFileInputRef = useRef<HTMLInputElement>(null);

  const handleAddEnvSubItem = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (!newEnvSubItemInput.trim()) return;
    setNewEnvSubItems([...newEnvSubItems, newEnvSubItemInput]);
    setNewEnvSubItemInput('');
  };

  const removeEnvSubItem = (index: number) => {
    setNewEnvSubItems(newEnvSubItems.filter((_, i) => i !== index));
  };

  const handleAddEnvironment = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!newEnvName) return;

    const numericValue = newEnvValueBrl ? Number(newEnvValueBrl.replace(/[^\d,]/g, '').replace(',', '.')) : 0;
    if (numericValue < 0) {
      setConfirmModal({
        title: 'Valor Inválido',
        message: 'O valor do ambiente não pode ser negativo.',
        onConfirm: () => setConfirmModal(null)
      });
      return;
    }

    const start = newEnvStartDate || order.suggestedDate || new Date().toISOString();

    const doAdd = () => {
      const envTech = allTechnicians.find(t => t.id === newEnvTechnicianId);

      const checklist: ChecklistItem[] = newEnvSubItems.length > 0
        ? newEnvSubItems.map((label) => ({ id: Math.random().toString(36), label, checked: false }))
        : [
          { id: Math.random().toString(36), label: 'Conferir peças', checked: false },
          { id: Math.random().toString(36), label: 'Limpeza do local', checked: false },
          { id: Math.random().toString(36), label: 'Montagem completa', checked: false }
        ];

      const newEnv: Environment = {
        id: Math.random().toString(36),
        name: newEnvName,
        estimatedDays: newEnvDays,
        status: 'PENDING',
        checklist: checklist,
        startDate: start,
        deadlineDate: calculateEndDate(start, newEnvDays),
        value_brl: numericValue,
        technicianId: envTech?.id,
        technicianName: envTech?.name,
        ...(newEnvProjectFile ? { projectFileUrl: newEnvProjectFile.url, projectFileName: newEnvProjectFile.name } : {})
      };

      let updatedEnvs = [];
      if (isEditing) {
         updatedEnvs = [...editForm.environments, newEnv];
         setEditForm(prev => ({ ...prev, environments: updatedEnvs }));
      } else {
         updatedEnvs = [...(order.environments || []), newEnv];
         onUpdateOrder({
            ...order,
            environments: updatedEnvs,
            updatedAt: new Date().toISOString(),
            history: [...order.history, { action: `Ambiente adicionado: ${newEnvName}`, timestamp: new Date().toISOString(), user: currentUser.name }]
         });
      }

      // Reset Form
      setNewEnvName('');
      setNewEnvDays(1);
      setNewEnvValueBrl('');
      setNewEnvStartDate('');
      setNewEnvTechnicianId('');
      setNewEnvSubItems([]);
      setNewEnvSubItemInput('');
      setNewEnvProjectFile(null);
      if (newEnvProjectFileInputRef.current) newEnvProjectFileInputRef.current.value = '';
    };

    if (numericValue === 0 && currentUser.role === 'ADMIN') {
      setConfirmModal({
        title: 'Confirmar Valor Zerado',
        message: 'Valor zerado, confirmar?',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        onConfirm: () => {
          setConfirmModal(null);
          doAdd();
        },
        onCancel: () => setConfirmModal(null)
      });
    } else {
      doAdd();
    }
  };

  useEffect(() => {
    if (isEditing) {
      setEditForm({
        clientName: order.clientName,
        clientPhone: order.clientPhone,
        address: order.address,
        cep: order.cep,
        referencePoint: order.referencePoint,
        description: order.description,
        suggestedDate: order.suggestedDate || '',
        suggestedTime: order.suggestedTime || '',
        assistanceChecklist: order.assistanceChecklist || [],
        assistanceOriginReason: order.assistanceOriginReason || '' as WorkOrder['assistanceOriginReason'] | '',
        environments: order.environments || []
      });
    }
  }, [isEditing, order]);

  const timeSlots = [];
  for (let i = 7; i <= 20; i++) {
    const hour = i.toString().padStart(2, '0');
    timeSlots.push(`${hour}:00`);
    if (i < 20) {
      timeSlots.push(`${hour}:30`);
    }
  }

  const [attachment, setAttachment] = useState<{ preview: string; type: 'image' | 'video' | 'audio' | 'document'; name?: string; mimeType?: string; duration?: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaFileInputRef = useRef<HTMLInputElement>(null);

  // Per-environment project file inputs
  const projectFileInputRefs = useRef<{ [idx: number]: HTMLInputElement | null }>({});

  // Audio Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [ratingCriteria, setRatingCriteria] = useState({
    punctuality: 0,
    care: 0,
    cleanliness: 0,
    communication: 0
  });
  const [ratingComment, setRatingComment] = useState('');

  const calculatedStars = Math.round(
    (ratingCriteria.punctuality + ratingCriteria.care + ratingCriteria.cleanliness + ratingCriteria.communication) / 4
  ) || 0;

  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab === 'CHAT') {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTab, order.comments]);

  const safeFormat = (dateInput: string | Date | undefined, fmt: string = 'dd/MM/yyyy') => {
    if (!dateInput) return '';
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (typeof dateInput === 'string' && dateInput.length === 10 && !dateInput.includes('T')) {
      const localD = new Date(dateInput + 'T00:00:00');
      return isValid(localD) ? format(localD, fmt) : 'Data Inválida';
    }
    return isValid(d) ? format(d, fmt) : 'Data Inválida';
  };

  const handleAssignTechnician = (techId: string) => {
    const tech = allTechnicians.find(t => t.id === techId);
    if (!tech) return;
    const newStatus = order.status === 'OPEN' ? 'ASSIGNED' : order.status;
    const updated: WorkOrder = {
      ...order,
      status: newStatus,
      technicianId: tech.id,
      technicianName: tech.name,
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: `Técnico alterado para ${tech.name}`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
  };

  const handleSaveEvent = async (eventData: any, files: File[]) => {
    try {
      if(editEventData) {
         await db.updateWorkOrderEvent(editEventData.id, eventData, files);
      } else {
         await db.createWorkOrderEvent(eventData, files);
      }
      await loadEvents();
      onUpdateOrder(
        { ...order, updatedAt: new Date().toISOString() }, 
        'ORDER_UPDATE', 
        editEventData ? `A ocorrência '${eventData.title}' foi atualizada.` : `A ocorrência '${eventData.title}' foi registrada com sucesso.`, 
        editEventData ? 'Ocorrência Atualizada' : 'Ocorrência Registrada'
      );
    } catch(e: any) {
      console.error(e);
      setConfirmModal({
        title: 'Erro ao Salvar',
        message: 'Erro ao salvar no banco. Verifique logs. Detalhes: ' + (e?.message || e),
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleDeleteEvent = (id: string, title: string) => {
     setConfirmModal({
       title: 'Excluir Ocorrência',
       message: `Tem certeza que deseja excluir a ocorrência '${title}'?`,
       confirmLabel: 'Excluir',
       cancelLabel: 'Cancelar',
       confirmStyle: 'danger',
       onConfirm: async () => {
         setConfirmModal(null);
         try {
           await db.deleteWorkOrderEvent(id, order.id, title);
           await loadEvents();
         } catch(e: any) {
           console.error(e);
           setConfirmModal({
             title: 'Erro ao Excluir',
             message: 'Erro ao excluir ocorrência. Detalhes: ' + (e?.message || e),
             onConfirm: () => setConfirmModal(null)
           });
         }
       },
       onCancel: () => setConfirmModal(null)
     });
  };

  const handleStatusChange = (newStatus: Status) => {
    const updated: WorkOrder = {
      ...order,
      status: newStatus,
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: `Status alterado para ${getStatusLabel(newStatus)}`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
  };

  const handleRequestReview = () => {
    const updated: WorkOrder = {
      ...order,
      status: 'PENDING_REVIEW',
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: `Serviço finalizado. Aguardando aprovação do cliente.`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
  };

  const handleSignatureSubmit = () => {
    if (signaturePassword !== currentUser.password) {
      setSignatureError('Senha incorreta.');
      return;
    }

    const updated: WorkOrder = {
      ...order,
      status: 'COMPLETED',
      updatedAt: new Date().toISOString(),
      clientConfirmation: {
        confirmedAt: new Date().toISOString(),
        method: 'PASSWORD'
      },
      history: [...order.history, { action: `Conclusão confirmada via Assinatura Digital`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
    setShowSignatureModal(false);
    setSignaturePassword('');
    if (currentUser.role === 'CLIENT') {
      setShowRatingPrompt(true);
    }
  };

  const handleGoToRating = () => {
    setShowRatingPrompt(false);
    setActiveTab('DETAILS');
    setTimeout(() => {
      ratingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleCancel = () => {
    if (!cancelReason) return;
    const updated: WorkOrder = {
      ...order,
      status: 'CANCELLED',
      cancellationReason: cancelReason,
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: `Cancelada. Motivo: ${cancelReason}`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
    setShowCancelModal(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(false);
    onDeleteOrder(order.id);
  };

  const handleSaveEdit = () => {
    const updated: WorkOrder = {
      ...order,
      ...editForm,
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: 'Dados da O.S. editados pelo Admin', timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
    setIsEditing(false);
  };

  const handleAddAssistanceItem = () => {
    if (!newItemText.trim()) return;
    const newItem: ChecklistItem = { id: Math.random().toString(36), label: newItemText, checked: false };
    setEditForm(prev => ({ ...prev, assistanceChecklist: [...prev.assistanceChecklist, newItem] }));
    setNewItemText('');
  };

  const handleRemoveAssistanceItem = (id: string) => {
    setEditForm(prev => ({ ...prev, assistanceChecklist: prev.assistanceChecklist.filter(item => item.id !== id) }));
  };

  const handleAddEnvItem = (envIndex: number) => {
    const text = envNewItemTexts[envIndex];
    if (!text?.trim()) return;
    const newEnvs = [...editForm.environments];
    const newItem: ChecklistItem = { id: Math.random().toString(36), label: text, checked: false };
    newEnvs[envIndex] = { ...newEnvs[envIndex], checklist: [...newEnvs[envIndex].checklist, newItem] };
    setEditForm(prev => ({ ...prev, environments: newEnvs }));
    setEnvNewItemTexts(prev => ({ ...prev, [envIndex]: '' }));
  };

  const handleRemoveEnvItem = async (envIndex: number, itemId: string) => {
    const newEnvs = [...editForm.environments];
    newEnvs[envIndex] = { ...newEnvs[envIndex], checklist: newEnvs[envIndex].checklist.filter(i => i.id !== itemId) };
    setEditForm(prev => ({ ...prev, environments: newEnvs }));
    await db.deleteChecklistItem(itemId);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessingFile(true);

    // Determine attachment type
    let type: 'image' | 'video' | 'audio' | 'document';
    if (file.type.startsWith('image/')) {
      type = 'image';
    } else if (file.type.startsWith('video/')) {
      type = 'video';
    } else if (file.type.startsWith('audio/')) {
      type = 'audio';
    } else {
      type = 'document';
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        setAttachment({
          preview: reader.result as string,
          type,
          name: file.name,
          mimeType: file.type
        });
      }
      setIsProcessingFile(false);
    };
    reader.onerror = () => {
      setConfirmModal({
        title: 'Erro de Leitura',
        message: 'Erro ao carregar arquivo.',
        onConfirm: () => setConfirmModal(null)
      });
      setIsProcessingFile(false);
    };
    reader.readAsDataURL(file);
  };

  // --- Document icon helper ---
  const getDocIcon = (fileName?: string, mimeType?: string) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    const mime = mimeType?.toLowerCase() || '';
    if (ext === 'pdf' || mime.includes('pdf')) return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50', label: 'PDF' };
    if (['doc', 'docx'].includes(ext) || mime.includes('word')) return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50', label: 'DOC' };
    if (['ppt', 'pptx'].includes(ext) || mime.includes('presentation') || mime.includes('powerpoint')) return { icon: FileText, color: 'text-orange-500', bg: 'bg-orange-50', label: 'PPT' };
    if (['xls', 'xlsx'].includes(ext) || mime.includes('spreadsheet') || mime.includes('excel')) return { icon: FileText, color: 'text-green-500', bg: 'bg-green-50', label: 'XLS' };
    if (['txt', 'csv', 'log'].includes(ext) || mime.includes('text/plain')) return { icon: FileText, color: 'text-gray-500', bg: 'bg-gray-50', label: 'TXT' };
    return { icon: FileIcon, color: 'text-gray-400', bg: 'bg-gray-50', label: ext.toUpperCase() || 'FILE' };
  };

  // --- Audio recording functions ---
  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            setAttachment({
              preview: reader.result as string,
              type: 'audio',
              name: `audio_${new Date().toISOString().replace(/[:.]/g, '-')}.webm`,
              mimeType: 'audio/webm',
              duration: recordingTime
            });
          }
        };
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setConfirmModal({
        title: 'Acesso ao Microfone Negado',
        message: 'Não foi possível acessar o microfone. Verifique as permissões do navegador.',
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    // Stop all tracks
    if (mediaRecorderRef.current?.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingTime(0);
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() && !attachment) return;

    const comment: Comment = {
      id: Math.random().toString(36),
      userId: currentUser.id,
      userName: currentUser.name,
      text: newMessage,
      timestamp: new Date(),
      ...(attachment ? {
        attachmentUrl: attachment.preview,
        attachmentType: attachment.type,
        attachmentName: attachment.name,
        attachmentMimeType: attachment.mimeType,
        ...(attachment.type === 'audio' && attachment.duration ? { audioDuration: attachment.duration } : {})
      } : {})
    };

    const updated: WorkOrder = {
      ...order,
      comments: [...order.comments, comment],
      updatedAt: new Date().toISOString()
    };

    // Build alert title and message with O.S. identity
    const alertTitle = `${order.id} · ${order.clientName}`;
    const msgPreview = newMessage.trim()
      ? `${currentUser.name}: "${newMessage.trim().substring(0, 70)}${newMessage.trim().length > 70 ? '...' : ''}"`
      : `${currentUser.name} enviou ${attachment?.type === 'audio' ? 'um áudio' : attachment?.type === 'image' ? 'uma imagem' : 'um arquivo'}`;

    onUpdateOrder(updated, 'CHAT_MESSAGE', msgPreview, alertTitle);

    setNewMessage('');
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (mediaFileInputRef.current) {
      mediaFileInputRef.current.value = '';
    }
  };

  const handleStartEnvironment = (envId: string) => {
    const updatedEnvironments = order.environments?.map(env =>
      env.id === envId
        ? { ...env, status: 'IN_PROGRESS' as const, startedAt: new Date().toISOString() }
        : env
    ) || [];

    // Recalcular status da OS
    const allCompleted = updatedEnvironments.every(e => e.status === 'COMPLETED');
    const anyInProgress = updatedEnvironments.some(e => e.status === 'IN_PROGRESS');
    const anyCompleted = updatedEnvironments.some(e => e.status === 'COMPLETED');

    const newOrderStatus = allCompleted
      ? 'COMPLETED'
      : (anyInProgress || anyCompleted)
        ? 'IN_PROGRESS'
        : order.status;

    const updatedOrder = {
      ...order,
      environments: updatedEnvironments,
      status: newOrderStatus,
      history: [
        ...(order.history || []),
        {
          action: `Ambiente iniciado`,
          timestamp: new Date().toISOString(),
          user: currentUser.name
        }
      ]
    };

    onUpdateOrder(updatedOrder);
  };

  const handleEnvironmentCheck = (envIndex: number, checkId: string) => {
    if (!order.environments) return;
    const newEnvs = [...order.environments];
    const env = newEnvs[envIndex];
    const item = env.checklist.find(c => c.id === checkId);
    if (item) {
      item.checked = !item.checked;
    }

    // Recalcular status do ambiente
    const allChecked = env.checklist.every(c => c.checked);
    const wasCompleted = env.status === 'COMPLETED';
    let history = [...(order.history || [])];

    if (allChecked) {
      if (env.status !== 'COMPLETED') {
        env.status = 'COMPLETED';
        env.completedDate = new Date().toISOString();
        history.push({
          action: `Ambiente ${env.name} finalizado`,
          timestamp: new Date().toISOString(),
          user: currentUser.name
        });
      }
    } else {
      const hasCheckedItems = env.checklist.some(c => c.checked);
      if (env.status === 'COMPLETED' || env.status === 'IN_PROGRESS' || env.startedAt || hasCheckedItems) {
        if (wasCompleted) {
          history.push({
            action: `Ambiente ${env.name} reaberto (checklist alterado)`,
            timestamp: new Date().toISOString(),
            user: currentUser.name
          });
        }
        env.status = 'IN_PROGRESS';
        env.completedDate = undefined;
      } else {
        env.status = 'PENDING';
      }
    }

    // Recalcular status da OS
    const allCompleted = newEnvs.every(e => e.status === 'COMPLETED');
    const anyInProgress = newEnvs.some(e => e.status === 'IN_PROGRESS');
    const anyCompleted = newEnvs.some(e => e.status === 'COMPLETED');

    const newOrderStatus = allCompleted
      ? 'COMPLETED'
      : (anyInProgress || anyCompleted)
        ? 'IN_PROGRESS'
        : order.status;

    const updated: WorkOrder = {
      ...order,
      environments: newEnvs,
      status: newOrderStatus,
      updatedAt: new Date().toISOString(),
      history
    };
    onUpdateOrder(updated);
  };

  const handleConfirmEnvStatusChange = async (envId: string, currentStatus: string, envName: string) => {
    if (newEnvStatusValue === currentStatus) {
      setEditingEnvStatusId(null);
      setNewEnvStatusValue('');
      setEnvStatusUpdateError(null);
      return;
    }

    setIsUpdatingStatus(true);
    setEnvStatusUpdateError(null);

    try {
      const updatePayload: any = { status: newEnvStatusValue };
      if (newEnvStatusValue === 'COMPLETED') {
        updatePayload.completedDate = new Date().toISOString();
      } else if (newEnvStatusValue === 'IN_PROGRESS') {
        updatePayload.completedDate = null;
        const currentEnv = (order.environments || []).find(e => e.id === envId);
        if (currentEnv && !currentEnv.startDate) {
          updatePayload.startDate = new Date().toISOString();
        }
      } else if (newEnvStatusValue === 'PENDING') {
        updatePayload.startDate = null;
        updatePayload.completedDate = null;
      }

      const { error } = await supabase
        .from('environments')
        .update(updatePayload)
        .eq('id', envId);

      if (error) {
        setEnvStatusUpdateError(error.message);
        setIsUpdatingStatus(false);
        return;
      }

      const updatedEnvironments = (order.environments || []).map(env =>
        env.id === envId
          ? { 
              ...env, 
              status: newEnvStatusValue as 'PENDING' | 'IN_PROGRESS' | 'COMPLETED',
              ...(newEnvStatusValue === 'COMPLETED' ? { completedDate: env.completedDate || new Date().toISOString() } : {}),
              ...(newEnvStatusValue === 'IN_PROGRESS' ? { startDate: env.startDate || new Date().toISOString(), completedDate: undefined } : {}),
              ...(newEnvStatusValue === 'PENDING' ? { startDate: undefined, completedDate: undefined } : {})
            }
          : env
      );

      const allCompleted = updatedEnvironments.every(e => e.status === 'COMPLETED');
      const anyInProgress = updatedEnvironments.some(e => e.status === 'IN_PROGRESS');
      const anyCompleted = updatedEnvironments.some(e => e.status === 'COMPLETED');

      const newOrderStatus = allCompleted
        ? 'COMPLETED'
        : (anyInProgress || anyCompleted)
          ? 'IN_PROGRESS'
          : 'PENDING';

      const getFriendlyStatus = (s: string) => {
        if (s === 'PENDING') return 'Pendente';
        if (s === 'IN_PROGRESS') return 'Em andamento';
        if (s === 'COMPLETED') return 'Concluído';
        return s;
      };

      const updatedOrder = {
        ...order,
        environments: updatedEnvironments,
        status: newOrderStatus,
        history: [
          ...(order.history || []),
          {
            action: `Status do ambiente "${envName}" alterado para "${getFriendlyStatus(newEnvStatusValue)}" pelo Admin`,
            timestamp: new Date().toISOString(),
            user: currentUser.name
          }
        ]
      };

      onUpdateOrder(updatedOrder);
      setEditingEnvStatusId(null);
      setNewEnvStatusValue('');
    } catch (err: any) {
      setEnvStatusUpdateError(err.message || 'Erro inesperado ao salvar.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAssistanceCheck = (itemId: string) => {
    if (!order.assistanceChecklist) return;
    const newChecklist = order.assistanceChecklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    const updated: WorkOrder = { ...order, assistanceChecklist: newChecklist };
    onUpdateOrder(updated);
  };

  const handleEnvironmentFinish = (envIndex: number) => {
    if (!order.environments) return;
    const newEnvs = [...order.environments];
    const env = newEnvs[envIndex];
    env.status = 'COMPLETED';
    env.completedDate = new Date().toISOString();

    // Recalcular status da OS
    const allCompleted = newEnvs.every(e => e.status === 'COMPLETED');
    const anyInProgress = newEnvs.some(e => e.status === 'IN_PROGRESS');
    const anyCompleted = newEnvs.some(e => e.status === 'COMPLETED');

    const newOrderStatus = allCompleted
      ? 'COMPLETED'
      : (anyInProgress || anyCompleted)
        ? 'IN_PROGRESS'
        : order.status;

    const updated: WorkOrder = {
      ...order,
      environments: newEnvs,
      status: newOrderStatus,
      updatedAt: new Date().toISOString(),
      history: [...order.history, { action: `Ambiente ${env.name} finalizado`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
  };

  const handleSubmitRating = () => {
    if (Object.values(ratingCriteria).some(val => val === 0)) {
      setConfirmModal({
        title: 'Avaliação Incompleta',
        message: 'Por favor, avalie todos os critérios.',
        onConfirm: () => setConfirmModal(null)
      });
      return;
    }
    const rating: Rating = {
      stars: calculatedStars,
      ...ratingCriteria,
      comment: ratingComment,
      createdAt: new Date().toISOString()
    };
    const updated: WorkOrder = {
      ...order,
      rating: rating,
      history: [...order.history, { action: `Avaliação registrada (${calculatedStars} estrelas)`, timestamp: new Date().toISOString(), user: currentUser.name }]
    };
    onUpdateOrder(updated);
  };

  const TabButton = ({ id, label, icon: Icon }: any) => (
    <button
      onClick={() => setActiveTab(id)}
      style={activeTab === id ? {
        borderBottom: '2px solid #1a6bff',
        color: '#00d4ff',
        background: 'rgba(26,107,255,.1)',
      } : {
        borderBottom: '2px solid transparent',
        color: 'var(--muted)',
      }}
      className="flex-1 py-3 text-sm font-medium flex justify-center items-center gap-2 transition-all"
    >
      <Icon size={16} />
      {label}
    </button>
  );

  const StarRating = ({ value, readOnly = false, onChange, size = 20 }: { value: number, readOnly?: boolean, onChange?: (val: number) => void, size?: number }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} disabled={readOnly} onClick={() => onChange && onChange(star)} className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'}`}>
          <Star size={size} className={`${star <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );

  const isSigned = !!order.clientConfirmation || order.status === 'COMPLETED';
  const isLockedForTech = isSigned && currentUser.role === 'TECHNICIAN';
  const canRequestApproval = order.type === 'ASSEMBLY' 
    ? (order.environments?.every(env => env.status === 'COMPLETED') ?? true)
    : (!order.assistanceChecklist || order.assistanceChecklist.length === 0 || order.assistanceChecklist.every(item => item.checked));

  const tabs = [
    { id: 'DETAILS' as const, label: 'Detalhes', icon: Clipboard },
    ...(order.type === 'ASSEMBLY' ? [{ id: 'ENVIRONMENTS' as const, label: 'Ambientes', icon: CheckSquare }] : []),
    { id: 'CHECKLIST' as const, label: 'Materiais/Ferramentas', icon: CheckSquare },
    { id: 'OCCURRENCES' as const, label: 'Ocorrências', icon: AlertCircle },
    { id: 'CHAT' as const, label: 'Chat / Mídia', icon: MessageCircle }
  ];

  const visibleTabs = tabs.filter(tab => {
    if (currentUser.role === 'CLIENT') {
      return ['ENVIRONMENTS', 'OCCURRENCES'].includes(tab.id);
    }
    return true;
  });

  const workOrderEvents = events;

  // Calcular schedule completo com atrasos
  const scheduledEnvironments = calculateEnvironmentSchedule(order, workOrderEvents || []);
  const orderEndDate = calculateOrderEndDate(scheduledEnvironments);

  // Dias estimados originais (sem atrasos)
  const diasEstimados = order.environments?.reduce((max, env) =>
    Math.max(max, env.estimatedDays || 1), 1) || 1;

  // Atraso total acumulado
  const diasAtraso = (workOrderEvents || [])
    .filter(ev => ev.impactDeadline && ev.impactWorkdays)
    .reduce((sum, ev) => sum + (ev.impactWorkdays || 0), 0);

  // Data de término recalculada
  const dataTermino = orderEndDate
    ? orderEndDate.toLocaleDateString('pt-BR')
    : '—';

  // Previsão total
  const diasTotal = diasEstimados + diasAtraso;

  // Progresso — baseado na data de início e hoje
  const inicio = order.suggestedDate ? new Date(order.suggestedDate + 'T00:00:00') : null;
  const hoje = new Date();
  const diasDecorridos = inicio
    ? Math.max(0, Math.ceil((hoje.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const progresso = diasTotal > 0 ? Math.min(100, Math.round((diasDecorridos / diasTotal) * 100)) : 0;

  // Soma dos impactCostBrl das ocorrências com impactCost true
  const custosExtras = (workOrderEvents || [])
    .filter(ev => ev.impactCost && ev.impactCostBrl)
    .reduce((sum, ev) => sum + (ev.impactCostBrl || 0), 0);

  // Valor original da OS
  const valorOriginal = order.environments?.reduce((sum, env) =>
    sum + (env.value_brl || 0), 0) || 0;

  // Valor total com acréscimos
  const valorTotal = valorOriginal + custosExtras;

  return (
    <div className="min-h-[calc(100vh-100px)] flex flex-col" style={{ background: 'transparent' }}>
      <div className="sticky top-0 z-20 flex flex-col" style={{ background: 'var(--bg)' }}>
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0" style={{ background: 'rgba(10,15,30,.95)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={onBack}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '4px', display: 'flex', alignItems: 'center', borderRadius: '6px', transition: 'color .15s' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--sf-white)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--muted)')}
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">{order.id}</h2>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${order.type === 'ASSEMBLY' ? 'bg-orange-100 text-orange-800' : 'bg-cyan-100 text-cyan-800'}`}>
                  {order.type === 'ASSEMBLY' ? 'MONTAGEM' : 'ASSISTÊNCIA'}
                </span>
                {order.type === 'ASSEMBLY' && (currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800">
                    Total: {order.environments?.reduce((sum, e) => sum + (e.value_brl || 0), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}
                  </span>
                )}
              </div>
              {isEditing ? (
                <div className="flex flex-col gap-2 mt-2 w-full max-w-sm">
                  <input 
                    type="text" 
                    value={editForm.clientName} 
                    onChange={e => setEditForm({ ...editForm, clientName: e.target.value })} 
                    className="w-full text-sm p-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 outline-none text-gray-900 bg-white" 
                    placeholder="Nome do Cliente"
                  />
                  <input 
                    type="text" 
                    value={editForm.clientPhone} 
                    onChange={e => setEditForm({ ...editForm, clientPhone: e.target.value })} 
                    className="w-full text-sm p-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 outline-none text-gray-900 bg-white" 
                    placeholder="Telefone do Cliente"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-sm text-gray-500 line-clamp-1">{order.clientName}</p>
                  {order.clientPhone && <p className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{order.clientPhone}</p>}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 w-full sm:w-auto border-t sm:border-none pt-2 sm:pt-0 border-gray-100">
            {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
              <div className="flex items-center gap-2 mr-2">
                {isEditing ? (
                  <button onClick={handleSaveEdit} className="p-2 text-green-600 bg-green-50 hover:bg-green-100 rounded-full" title="Salvar Edição"><Save size={20} /></button>
                ) : (
                  <button onClick={() => setIsEditing(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full" title="Editar Ordem"><Pencil size={20} /></button>
                )}
                <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:bg-red-50 rounded-full" title="Excluir Ordem"><Trash size={20} /></button>
              </div>
            )}
            <div style={{
              padding: '2px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', display: 'inline-block',
              ...(order.status === 'COMPLETED' ? { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' }
               : order.status === 'IN_PROGRESS' ? { background: 'rgba(26,107,255,.15)', color: '#60a5fa', border: '1px solid rgba(26,107,255,.35)' }
               : order.status === 'CANCELLED'   ? { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' }
               : order.status === 'PENDING_REVIEW' ? { background: 'rgba(139,92,246,.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.35)' }
               : order.status === 'ASSIGNED'    ? { background: 'rgba(245,158,11,.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,.3)' }
               : { background: 'rgba(0,212,255,.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,.3)' })
            }}>{getStatusLabel(order.status)}</div>
          </div>
        </div>

        <div className="flex" style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          {visibleTabs.map(tab => (
            <TabButton key={tab.id} id={tab.id} label={tab.label} icon={tab.icon} />
          ))}
        </div>
      </div>

      <div className="flex-1 p-4 overflow-y-auto" style={{ background: 'transparent' }}>
        {activeTab === 'DETAILS' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', position: 'relative', paddingTop: 48 }}>

            {/* ── Botão Gerenciar Ordem e Dropdown ── */}
            <div style={{ position: 'absolute', top: 0, right: 0, zIndex: 50 }}>
              <button
                onClick={() => setShowQuickActions(!showQuickActions)}
                style={{
                  padding: '8px 16px',
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--sf-white)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                  e.currentTarget.style.background = 'var(--bg2)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.background = 'var(--bg3)';
                }}
              >
                <UserCheck size={16} />
                Gerenciar Ordem
              </button>

              {showQuickActions && (
                <div style={{
                  position: 'absolute',
                  top: '42px',
                  right: 0,
                  width: '280px',
                  background: 'var(--bg2)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  zIndex: 50
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserCheck size={14} /> Ações Rápidas
                  </p>
                  
                  {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (order.status === 'OPEN' || order.status === 'ASSIGNED') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>
                        {order.technicianId ? 'Alterar Técnico Responsável' : 'Atribuir Técnico'}
                      </label>
                      <select 
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} 
                        onChange={(e) => handleAssignTechnician(e.target.value)} 
                        value={order.technicianId || ""}
                      >
                        <option value="" disabled>Selecione um técnico...</option>
                        {allTechnicians.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                      </select>
                    </div>
                  )}

                  {((currentUser.role === 'TECHNICIAN' && (order.technicianId === currentUser.id || order.environments?.some(e => e.technicianId === currentUser.id))) || currentUser.role === 'ADMIN') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {order.status === 'ASSIGNED' && order.type !== 'ASSEMBLY' && (
                        <Button onClick={() => { handleStatusChange('IN_PROGRESS'); setShowQuickActions(false); }} fullWidth>
                          Iniciar Serviço
                        </Button>
                      )}
                      {order.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => { handleRequestReview(); setShowQuickActions(false); }}
                          disabled={!canRequestApproval}
                          className={`px-4 py-2 rounded-lg text-sm font-medium w-full flex items-center justify-center gap-2 transition-all ${canRequestApproval ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-70'}`}
                          title={!canRequestApproval ? (order.type === 'ASSEMBLY' ? 'Finalize todos os ambientes primeiro.' : 'Conclua o checklist primeiro.') : ''}
                        >
                          <UserCheck size={16} /> Solicitar Aprovação
                        </button>
                      )}
                      {currentUser.role === 'ADMIN' && order.status === 'PENDING_REVIEW' && (
                        <button 
                          onClick={() => { handleStatusChange('COMPLETED'); setShowQuickActions(false); }} 
                          className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 w-full"
                        >
                          Forçar Conclusão (Admin)
                        </button>
                      )}
                    </div>
                  )}

                  {currentUser.role === 'CLIENT' && order.status === 'PENDING_REVIEW' && (
                    <div style={{ background: 'rgba(251,146,60,.08)', border: '1px solid rgba(251,146,60,.3)', borderRadius: 10, padding: 12 }}>
                      <h4 style={{ fontWeight: 700, color: '#fb923c', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <AlertTriangle size={16} /> Aprovação Necessária
                      </h4>
                      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                        O técnico informou que o serviço foi concluído. Por favor, confirme a execução.
                      </p>
                      <button 
                        onClick={() => { setShowSignatureModal(true); setShowQuickActions(false); }} 
                        style={{ width: '100%', padding: '10px 0', background: '#16a34a', color: '#fff', fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13 }}
                      >
                        <CheckCircle size={16} /> Aprovar e Concluir
                      </button>
                    </div>
                  )}

                  {order.status === 'COMPLETED' && order.clientConfirmation && (
                    <div style={{ background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.25)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#34d399' }}>
                      <Lock size={14} /> Assinado digitalmente
                    </div>
                  )}

                  {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                      <Button variant="danger" fullWidth onClick={() => { setShowCancelModal(true); setShowQuickActions(false); }}>
                        Cancelar Ordem
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Seção 1 — Informações da O.S. (grid 3 colunas) ── */}
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
                {/* Coluna 1 — Cliente */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserIcon size={16} /> CLIENTE
                  </p>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} placeholder="Nome do Cliente" value={editForm.clientName} onChange={e => setEditForm({ ...editForm, clientName: e.target.value })} />
                      <input style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} placeholder="Telefone" value={editForm.clientPhone} onChange={e => setEditForm({ ...editForm, clientPhone: e.target.value })} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sf-white)' }}>{order.clientName || '—'}</span>
                      {order.clientEmail && (
                        <span style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Mail size={14} style={{ color: 'var(--sf-blue)', flexShrink: 0 }} /> {order.clientEmail}
                        </span>
                      )}
                      {order.clientPhone && (
                        <span style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Phone size={14} style={{ color: 'var(--sf-blue)', flexShrink: 0 }} /> {order.clientPhone}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Coluna 2 — Endereço */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MapPin size={16} /> ENDEREÇO
                  </p>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} placeholder="CEP" value={editForm.cep} onChange={e => setEditForm({ ...editForm, cep: e.target.value })} />
                      <input style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} placeholder="Endereço Completo" value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} />
                      <input style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }} placeholder="Ponto de Referência" value={editForm.referencePoint} onChange={e => setEditForm({ ...editForm, referencePoint: e.target.value })} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--sf-white)' }}>{order.address || '—'}</span>
                      {order.cep && <span style={{ fontSize: 12, color: 'var(--muted)' }}>CEP: {order.cep}</span>}
                      {order.referencePoint && <span style={{ fontSize: 11, color: 'var(--muted)', background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4, width: 'fit-content' }}>Ref: {order.referencePoint}</span>}
                    </div>
                  )}
                </div>

                {/* Coluna 3 — Agendamento */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={16} /> AGENDAMENTO
                  </p>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <DatePicker label="Data Sugerida" value={editForm.suggestedDate} onChange={d => setEditForm({ ...editForm, suggestedDate: d })} />
                      <div style={{ position: 'relative' }}>
                        <select value={editForm.suggestedTime} onChange={e => setEditForm({ ...editForm, suggestedTime: e.target.value })} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13, appearance: 'none' }}>
                          <option value="">Selecione...</option>
                          {timeSlots.map(time => (<option key={time} value={time}>{time}</option>))}
                        </select>
                        <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--muted)' }} size={14} />
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', flex: 1 }}>
                        <Calendar size={14} style={{ color: 'var(--sf-blue)', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: order.suggestedDate ? 'var(--sf-white)' : 'var(--muted)', fontWeight: 500 }}>
                          {order.suggestedDate ? safeFormat(order.suggestedDate) : 'Sem data'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', flex: 1 }}>
                        <Clock size={14} style={{ color: 'var(--sf-blue)', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: order.suggestedTime ? 'var(--sf-white)' : 'var(--muted)', fontWeight: 500 }}>
                          {order.suggestedTime || 'Sem hora'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {order.type === 'ASSEMBLY' && (diasEstimados > 0 || custosExtras > 0) && (
              <div style={{
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 16,
              }}>
                {/* Cabeçalho */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="14" height="14" fill="none" stroke="var(--blue)" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Resumo Operacional</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: custosExtras > 0 ? '1fr 1fr' : '1fr', gap: 0 }}>

                  {/* PRAZO */}
                  <div style={{ padding: '14px 16px', borderRight: custosExtras > 0 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Prazo estimado</div>
                        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                          {diasTotal} dias úteis
                        </div>
                      </div>
                      {diasAtraso > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Atraso acumulado</div>
                          <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 18, fontWeight: 700, color: 'var(--red)' }}>
                            + {diasAtraso} dias
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Barra de progresso */}
                    {inicio && (
                      <>
                        <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                          <div style={{
                            height: '100%',
                            width: `${progresso}%`,
                            borderRadius: 3,
                            background: progresso >= 100 ? 'var(--red)' : progresso >= 75 ? 'var(--amber)' : 'var(--green)',
                            transition: 'width .4s ease',
                          }} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {diasDecorridos} de {diasTotal} dias decorridos ({progresso}%)
                          {diasAtraso > 0 && (
                            <span style={{ color: 'var(--red)', marginLeft: 8 }}>
                              · {diasAtraso} dia{diasAtraso > 1 ? 's' : ''} de atraso por ocorrência{diasAtraso > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {/* Cronograma por ambiente */}
                    {scheduledEnvironments.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                          Cronograma por ambiente
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {scheduledEnvironments.map(env => {
                            const hasDelay = (env.adjusted_days || 0) > (env.estimatedDays || 0);
                            const endDate = env.start_date
                              ? (() => {
                                  const d = new Date(env.start_date + 'T00:00:00');
                                  d.setDate(d.getDate() + (env.adjusted_days || env.estimatedDays || 1) - 1);
                                  return d.toLocaleDateString('pt-BR');
                                })()
                              : '—';

                            return (
                              <div key={env.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: hasDelay ? 'rgba(239, 68, 68, 0.05)' : 'rgba(255, 255, 255, 0.02)', borderRadius: 7, padding: '8px 12px',
                                border: `1px solid ${hasDelay ? 'rgba(239, 68, 68, 0.35)' : 'var(--border)'}`,
                              }}>
                                <div>
                                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{env.name}</span>
                                  {hasDelay && (
                                    <span style={{ fontSize: 10, color: 'var(--sf-red)', marginLeft: 6, fontWeight: 600 }}>
                                      +{(env.adjusted_days || 0) - (env.estimatedDays || 0)}d atraso
                                    </span>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--muted)' }}>
                                  {env.start_date
                                    ? `${new Date(env.start_date + 'T00:00:00').toLocaleDateString('pt-BR')} → ${endDate}`
                                    : `${env.adjusted_days || env.estimatedDays || 1} dias`
                                  }
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {orderEndDate && (
                          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg3)', borderRadius: 7, border: '1px solid var(--border)' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Previsão de entrega</span>
                            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: diasAtraso > 0 ? 'var(--sf-red)' : 'var(--sf-green)' }}>
                              {orderEndDate.toLocaleDateString('pt-BR')}
                              {diasAtraso > 0 && ` (+${diasAtraso}d)`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CUSTOS EXTRAS */}
                  {custosExtras > 0 && (
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Valor original</div>
                          <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                            {valorOriginal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Custos extras</div>
                          <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 18, fontWeight: 700, color: 'var(--red)' }}>
                            + {custosExtras.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </div>
                        </div>
                      </div>
                      <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Total com acréscimos</span>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 16, fontWeight: 700, color: 'var(--amber)' }}>
                          {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>
                      {/* Lista de ocorrências com custo */}
                      <div style={{ marginTop: 10 }}>
                        {(workOrderEvents || [])
                          .filter(ev => ev.impactCost && ev.impactCostBrl)
                          .map(ev => (
                            <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', padding: '3px 0' }}>
                              <span>· {ev.title}</span>
                              <span style={{ fontFamily: 'DM Mono, monospace', color: 'var(--red)' }}>
                                + {ev.impactCostBrl?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Seção 2 — Descrição ── */}
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={16} /> DESCRIÇÃO
              </p>
              {isEditing ? (
                <textarea 
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    border: '1px solid var(--border)', 
                    borderRadius: 8, 
                    background: 'var(--bg3)', 
                    color: 'var(--sf-white)', 
                    fontSize: 13, 
                    lineHeight: 1.5, 
                    minHeight: 100, 
                    resize: 'vertical', 
                    boxSizing: 'border-box' 
                  }} 
                  value={editForm.description} 
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })} 
                  placeholder="Sem descrição"
                />
              ) : (
                <p style={{ 
                  fontSize: 13, 
                  color: order.description ? 'var(--sf-white)' : 'var(--muted)', 
                  lineHeight: 1.6, 
                  background: 'var(--bg3)', 
                  border: '1px solid var(--border)', 
                  borderRadius: 8, 
                  padding: '10px 14px', 
                  fontStyle: order.description ? 'normal' : 'italic', 
                  margin: 0 
                }}>
                  {order.description || 'Sem descrição'}
                </p>
              )}

              {/* Lógica e dados adicionais de assistência se for do tipo ASSISTANCE */}
              {order.type === 'ASSISTANCE' && (
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <UserIcon size={12} /> Dados da Assistência
                    </p>
                    <div style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span>Categoria: <span style={{ color: 'var(--muted)' }}>{order.assistanceCategory || 'Não classificada'}</span></span>
                      {order.assistanceCategoryDetail && <span>Detalhe: <span style={{ color: 'var(--muted)' }}>"{order.assistanceCategoryDetail}"</span></span>}
                      {isEditing ? (
                        <div style={{ marginTop: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Origem da Falha</label>
                          <select 
                            value={editForm.assistanceOriginReason || ''} 
                            onChange={e => setEditForm({ ...editForm, assistanceOriginReason: e.target.value as WorkOrder['assistanceOriginReason'] | '' })} 
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13 }}
                          >
                            <option value="">Não classificada</option>
                            <option value="OPERATIONAL">Operacional</option>
                            <option value="FACTORY">Fábrica</option>
                            <option value="TRANSPORT">Transporte</option>
                            <option value="PROJECT">Projeto</option>
                          </select>
                        </div>
                      ) : order.assistanceOriginReason ? (
                        <span>Origem: <span style={{ 
                          fontWeight: 600, 
                          padding: '1px 8px', 
                          borderRadius: 4, 
                          fontSize: 11, 
                          ...(order.assistanceOriginReason === 'OPERATIONAL' ? { background: 'rgba(251,146,60,.15)', color: '#fb923c' } 
                            : order.assistanceOriginReason === 'FACTORY' ? { background: 'rgba(239,68,68,.15)', color: '#f87171' } 
                            : order.assistanceOriginReason === 'TRANSPORT' ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' } 
                            : { background: 'rgba(167,139,250,.15)', color: '#a78bfa' }) 
                        }}>
                          {{ OPERATIONAL: 'Operacional', FACTORY: 'Fábrica', TRANSPORT: 'Transporte', PROJECT: 'Projeto' }[order.assistanceOriginReason]}
                        </span></span>
                      ) : null}
                    </div>
                  </div>

                  {order.isPostAssembly && order.relatedAssemblyWorkOrderId && (
                    <div style={{ background: 'rgba(26,107,255,.08)', border: '1px solid rgba(26,107,255,.25)', borderRadius: 8, padding: '10px 14px' }}>
                      <p style={{ fontWeight: 700, color: '#60a5fa', fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CheckCircle size={13} /> Origem Pós-Montagem
                      </p>
                      <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>O.S. de Origem: <strong style={{ color: 'var(--sf-white)' }}>{order.relatedAssemblyWorkOrderId}</strong></span>
                        <span>Ambiente Afetado: <strong style={{ color: 'var(--sf-white)' }}>{order.relatedEnvironmentName || 'Não informado'}</strong></span>
                        {order.relatedEnvironmentId && <span style={{ fontSize: 11 }}>Cód. Ambiente: {order.relatedEnvironmentId}</span>}
                      </div>
                    </div>
                  )}

                  {(order.suggestedDate || order.suggestedTime || isEditing) && (
                    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Calendar size={12} /> Agendamento da Assistência
                      </p>
                      {isEditing ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <DatePicker label="Data Sugerida" value={editForm.suggestedDate} onChange={d => setEditForm({ ...editForm, suggestedDate: d })} />
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Horário</label>
                            <div style={{ position: 'relative' }}>
                              <select 
                                value={editForm.suggestedTime} 
                                onChange={e => setEditForm({ ...editForm, suggestedTime: e.target.value })} 
                                style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg3)', color: 'var(--sf-white)', fontSize: 13, appearance: 'none' }}
                              >
                                <option value="">Selecione...</option>
                                {timeSlots.map(time => (<option key={time} value={time}>{time}</option>))}
                              </select>
                              <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--muted)' }} size={14} />
                            </div>
                          </div>
                        </div>
                      ) : ((order.suggestedDate || order.suggestedTime) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', width: 'fit-content' }}>
                          <Calendar size={13} style={{ color: 'var(--sf-blue)' }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sf-white)' }}>
                            {safeFormat(order.suggestedDate)} {order.suggestedTime && `às ${order.suggestedTime}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <CheckSquare size={12} /> Checklist de Execução
                    </p>
                    {!isEditing && order.assistanceChecklist && (<ProgressBar items={order.assistanceChecklist} />)}
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {editForm.assistanceChecklist.map(item => (
                          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' }}>
                            <span style={{ flex: 1, fontSize: 13, color: 'var(--sf-white)' }}>{item.label}</span>
                            <button type="button" onClick={() => handleRemoveAssistanceItem(item.id)} style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                              <Trash size={13} />
                            </button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                          <input 
                            type="text" 
                            value={newItemText} 
                            onChange={(e) => setNewItemText(e.target.value)} 
                            placeholder="Novo serviço..." 
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg2)', color: 'var(--sf-white)', fontSize: 13 }} 
                          />
                          <button type="button" onClick={handleAddAssistanceItem} style={{ background: 'var(--sf-blue)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    ) : ((order.assistanceChecklist && order.assistanceChecklist.length > 0) ? (
                      order.assistanceChecklist.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                          <button 
                            disabled={isLockedForTech || (currentUser.role !== 'TECHNICIAN' && currentUser.role !== 'ADMIN')} 
                            onClick={() => handleAssistanceCheck(item.id)} 
                            style={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: 4, 
                              border: item.checked ? 'none' : '2px solid rgba(255,255,255,.3)', 
                              flexShrink: 0, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              background: item.checked ? 'var(--sf-blue)' : 'transparent', 
                              cursor: (isLockedForTech || (currentUser.role !== 'TECHNICIAN' && currentUser.role !== 'ADMIN')) ? 'not-allowed' : 'pointer', 
                              opacity: (isLockedForTech || (currentUser.role !== 'TECHNICIAN' && currentUser.role !== 'ADMIN')) ? 0.6 : 1 
                            }}
                          >
                            {item.checked && <CheckSquare size={12} color="#fff" />}
                          </button>
                          <span style={{ fontSize: 13, color: item.checked ? 'var(--muted)' : 'var(--sf-white)', textDecoration: item.checked ? 'line-through' : 'none' }}>
                            {item.label}
                          </span>
                        </div>
                      ))
                    ) : (<p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>Nenhum item no checklist.</p>))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Seção 3 — Ambientes & Tarefas (só ASSEMBLY, admin/assistant) ── */}
            {order.type === 'ASSEMBLY' && order.environments && order.environments.length > 0 && (currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <LayoutGrid size={16} /> AMBIENTES &amp; TAREFAS
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {order.environments.map(env => (
                    <div 
                      key={env.id} 
                      style={{ 
                        background: 'var(--bg2)', 
                        border: '1px solid var(--border)', 
                        borderRadius: 12, 
                        padding: 16, 
                        transition: 'border-color .15s ease' 
                      }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,.25)')}
                      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <span style={{ fontSize: 14, fontWeight: 'bold', color: 'var(--sf-white)' }}>{env.name}</span>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          {env.technicianName && (
                            <span style={{ 
                                fontSize: 11, 
                                fontWeight: 600, 
                                background: 'rgba(139,92,246,.2)', 
                                color: '#fff', 
                                borderRadius: 4, 
                                padding: '2px 8px',
                                display: 'inline-block'
                              }}>
                              {env.technicianName}
                            </span>
                          )}
                        </div>
                        <div style={{ flex: 1, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                          {env.estimated_days && (
                            <span style={{ 
                                fontSize: 11, 
                                color: 'var(--muted)', 
                                background: 'var(--bg3)', 
                                border: '1px solid var(--border)', 
                                borderRadius: 4, 
                                padding: '2px 8px' 
                              }}>
                              Est. {env.estimated_days}d
                            </span>
                          )}
                          {env.value_brl !== undefined && env.value_brl > 0 && (
                            <span style={{ 
                                fontSize: 11, 
                                fontWeight: 600, 
                                color: '#34d399', 
                                background: 'rgba(16,185,129,.1)', 
                                border: '1px solid rgba(16,185,129,.25)', 
                                borderRadius: 4, 
                                padding: '2px 8px' 
                              }}>
                              {env.value_brl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'rgba(16,185,129,.06)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(52,211,153,.7)', letterSpacing: '.05em' }}>Total Geral da Montagem</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#34d399' }}>
                    {order.environments.reduce((sum, e) => sum + (e.value_brl || 0), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </div>
              </div>
            )}

            {/* ── Histórico ── */}
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={16} /> HISTÓRICO
              </p>
              <div style={{ borderLeft: '2px solid var(--border)', paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {order.history.filter(h => { if (currentUser.role === 'TECHNICIAN' && h.action.includes('Avaliação')) return false; return true; }).map((h, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: -23, top: 4, width: 10, height: 10, borderRadius: '50%', background: 'var(--border)', border: '2px solid var(--bg2)' }} />
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--sf-white)', margin: 0 }}>{h.action}</p>
                    <p style={{ fontSize: 11, color: 'var(--muted)', margin: '2px 0 0' }}>{safeFormat(h.timestamp, 'dd/MM/yyyy HH:mm')} · {h.user}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Avaliação ── */}
            {order.status === 'COMPLETED' && (currentUser.role === 'ADMIN' || (currentUser.role === 'CLIENT' && !order.rating)) && (
              <div ref={ratingRef} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Star size={16} /> AVALIAÇÃO DO ATENDIMENTO
                </p>
                {order.rating ? (
                  <div style={{ background: 'rgba(250,204,21,.06)', border: '1px solid rgba(250,204,21,.2)', borderRadius: 8, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div><StarRating value={order.rating.stars} readOnly /><span style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700, display: 'block', marginTop: 2 }}>Média Geral</span></div>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{safeFormat(order.rating.createdAt)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, borderTop: '1px solid rgba(250,204,21,.15)', paddingTop: 10, marginTop: 4 }}>
                      <div><span style={{ fontWeight: 600, color: 'var(--sf-white)', display: 'block', marginBottom: 2, fontSize: 12 }}>Pontualidade</span><StarRating value={order.rating.punctuality || 0} readOnly size={12} /></div>
                      <div><span style={{ fontWeight: 600, color: 'var(--sf-white)', display: 'block', marginBottom: 2, fontSize: 12 }}>Cuidado (Casa/Móveis)</span><StarRating value={order.rating.care || 0} readOnly size={12} /></div>
                      <div><span style={{ fontWeight: 600, color: 'var(--sf-white)', display: 'block', marginBottom: 2, fontSize: 12 }}>Limpeza e Organização</span><StarRating value={order.rating.cleanliness || 0} readOnly size={12} /></div>
                      <div><span style={{ fontWeight: 600, color: 'var(--sf-white)', display: 'block', marginBottom: 2, fontSize: 12 }}>Comunicação</span><StarRating value={order.rating.communication || 0} readOnly size={12} /></div>
                    </div>
                    {order.rating.comment && (<p style={{ fontSize: 13, color: 'var(--sf-white)', fontStyle: 'italic', borderTop: '1px solid rgba(250,204,21,.15)', paddingTop: 10, marginTop: 10 }}>"{order.rating.comment}"</p>)}
                    <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Avaliado por: {order.clientName}</p>
                  </div>
                ) : (currentUser.role === 'CLIENT' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ fontSize: 13, color: 'var(--muted)', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', margin: 0 }}>Como você avalia o serviço prestado? Sua opinião é muito importante.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Pontualidade</span><StarRating value={ratingCriteria.punctuality} onChange={(val) => setRatingCriteria({ ...ratingCriteria, punctuality: val })} /></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', alignItems: 'center', gap: 6 }}><PackageCheck size={14} /> Cuidado com Móveis</span><StarRating value={ratingCriteria.care} onChange={(val) => setRatingCriteria({ ...ratingCriteria, care: val })} /></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', alignItems: 'center', gap: 6 }}><Sparkles size={14} /> Limpeza e Organização</span><StarRating value={ratingCriteria.cleanliness} onChange={(val) => setRatingCriteria({ ...ratingCriteria, cleanliness: val })} /></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', alignItems: 'center', gap: 6 }}><MessageCircle size={14} /> Comunicação</span><StarRating value={ratingCriteria.communication} onChange={(val) => setRatingCriteria({ ...ratingCriteria, communication: val })} /></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sf-white)' }}>Média Geral:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24' }}>{calculatedStars}</span><Star size={18} style={{ fill: '#fbbf24', color: '#fbbf24' }} /></div>
                      </div>
                    </div>
                    <textarea style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 13, background: 'var(--bg3)', color: 'var(--sf-white)', boxSizing: 'border-box', minHeight: 80, resize: 'vertical' }} rows={3} placeholder="Deixe um comentário adicional (opcional)..." value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} />
                    <button onClick={handleSubmitRating} disabled={Object.values(ratingCriteria).some(v => v === 0)} style={{ width: '100%', padding: '10px 0', background: '#ca8a04', color: '#fff', fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, opacity: Object.values(ratingCriteria).some(v => v === 0) ? 0.5 : 1 }}>Enviar Avaliação Completa</button>
                  </div>
                ) : (<p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>Cliente ainda não avaliou este serviço.</p>))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'ENVIRONMENTS' && order.type === 'ASSEMBLY' && (
          <div className="space-y-4 w-full">
            {(isEditing ? editForm.environments : order.environments)?.map((env, idx) => {
              if (!isEditing && currentUser.role === 'TECHNICIAN' && order.technicianId !== currentUser.id) {
                if (env.technicianId !== currentUser.id) return null;
              }
              const hasProjectFile = env.projectFileUrl && typeof env.projectFileUrl === 'string' && env.projectFileUrl !== 'null' && env.projectFileUrl !== 'undefined' && env.projectFileUrl !== '';
              return (
                <div key={env.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 relative">
                  {isEditing ? (
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <input
                          type="text"
                          value={env.name}
                          onChange={(e) => {
                            const newEnvs = [...editForm.environments];
                            newEnvs[idx].name = e.target.value;
                            setEditForm({ ...editForm, environments: newEnvs });
                          }}
                          className="w-full text-lg font-bold p-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 outline-none text-gray-900 bg-white mb-2"
                          placeholder="Nome do Ambiente"
                        />
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                             <Calendar size={14} className="text-brand-500" />
                             <span className="text-xs text-gray-500 font-bold">Iniciado em:</span>
                             <input 
                                type="date"
                                className="p-1 px-2 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-brand-500 outline-none text-gray-900 shadow-sm"
                                value={env.startDate ? env.startDate.substring(0, 10) : ''}
                                onChange={(e) => {
                                  const newEnvs = [...editForm.environments];
                                  newEnvs[idx].startDate = e.target.value ? new Date(e.target.value + 'T12:00:00.000Z').toISOString() : undefined;
                                  if (newEnvs[idx].startDate && newEnvs[idx].estimatedDays) {
                                      newEnvs[idx].deadlineDate = calculateEndDate(newEnvs[idx].startDate!, newEnvs[idx].estimatedDays);
                                  }
                                  setEditForm({ ...editForm, environments: newEnvs });
                                }}
                             />
                          </div>
                          <div className="flex items-center gap-2"> <Calendar size={14} className="text-gray-500" /> <span className="text-xs text-gray-500">Previsão:</span> <input type="number" min="1" className="w-16 p-1 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-brand-500 outline-none text-gray-900" value={env.estimatedDays} onChange={(e) => { const val = parseInt(e.target.value) || 1; const newEnvs = [...editForm.environments]; newEnvs[idx].estimatedDays = val; const start = newEnvs[idx].startDate || new Date().toISOString(); newEnvs[idx].deadlineDate = calculateEndDate(start, val); setEditForm({ ...editForm, environments: newEnvs }); }} /> <span className="text-xs text-gray-500">dias úteis</span> </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-1"> <UserIcon size={14} className="text-gray-500" /> <span className="text-xs text-gray-500">Técnico:</span> <select className="p-1 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-brand-500 outline-none text-gray-900 w-40" value={env.technicianId || ''} onChange={(e) => { const newTechId = e.target.value; const newTech = allTechnicians.find(t => t.id === newTechId); const newEnvs = [...editForm.environments]; newEnvs[idx].technicianId = newTechId; newEnvs[idx].technicianName = newTech?.name; setEditForm({ ...editForm, environments: newEnvs }); }} > <option value="">Não atribuído</option> {allTechnicians.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))} </select> </div>
                            {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Valor R$:</span>
                                <input type="number" step="0.01" min="0" className="w-24 p-1 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-brand-500 outline-none text-gray-900" value={env.value_brl || ''} onChange={(e) => { const val = parseFloat(e.target.value) || 0; const newEnvs = [...editForm.environments]; newEnvs[idx].value_brl = val; setEditForm({ ...editForm, environments: newEnvs }); }} />
                              </div>
                            )}
                          </div>
                          {env.status === 'COMPLETED' && (
                             <div className="flex items-center gap-2 border-t border-gray-100 pt-2.5">
                               <CheckCircle size={14} className="text-green-500" />
                               <span className="text-xs text-gray-500 font-bold">Concluído em:</span>
                               <input 
                                  type="date"
                                  className="p-1 px-2 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-brand-500 outline-none text-gray-900 shadow-sm"
                                  value={env.completedDate ? env.completedDate.substring(0, 10) : ''}
                                  onChange={(e) => {
                                    const newEnvs = [...editForm.environments];
                                    newEnvs[idx].completedDate = e.target.value ? new Date(e.target.value + 'T12:00:00.000Z').toISOString() : undefined;
                                    setEditForm({ ...editForm, environments: newEnvs });
                                  }}
                               />
                             </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Restructured Header Row */}
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-bold text-gray-900" style={{ fontSize: '14px', margin: 0 }}>
                          {env.name}
                        </h3>
                        <div className="flex items-center gap-[12px]">
                          {/* Badge de status */}
                          {env.status === 'COMPLETED' ? (
                            <span className="flex items-center gap-1 text-xs font-bold px-[10px] py-[4px] rounded-full h-fit flex-shrink-0" style={{ color: 'var(--sf-green)', background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.2)' }}>
                              <CheckCircle size={12} /> Concluído
                            </span>
                          ) : env.status === 'IN_PROGRESS' ? (
                            <span className="flex items-center gap-1 text-xs font-bold px-[10px] py-[4px] rounded-full h-fit flex-shrink-0" style={{ color: 'var(--blue)', background: 'rgba(26,107,255,.15)', border: '1px solid rgba(26,107,255,.2)' }}>
                              <Clock size={12} /> Em andamento
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs font-bold px-[10px] py-[4px] rounded-full h-fit flex-shrink-0" style={{ color: 'var(--sf-amber)', background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.2)' }}>
                              Pendente
                            </span>
                          )}

                          {/* Pencil button + Popover compact container */}
                          {(currentUser.role === 'ADMIN' || currentUser.role === 'SUPERADMIN') && (
                            <div className="relative status-popover-container" style={{ display: 'inline-block' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingEnvStatusId(env.id);
                                  setNewEnvStatusValue(env.status);
                                  setEnvStatusUpdateError(null);
                                }}
                                className="flex items-center justify-center p-1 rounded-full transition-colors duration-200"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'var(--muted)',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted)')}
                                title="Alterar status do ambiente"
                              >
                                <Pencil size={16} />
                              </button>

                              {/* Compact status selector popover */}
                              {editingEnvStatusId === env.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    width: '260px',
                                    zIndex: 50,
                                    background: 'var(--bg2)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '10px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                    padding: '16px',
                                  }}
                                  className="animate-fade-in"
                                >
                                  <div style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px', textAlign: 'left' }}>
                                    ALTERAR STATUS
                                  </div>
                                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#fff', marginBottom: '12px', textAlign: 'left' }} className="truncate">
                                    {env.name}
                                  </div>

                                  <select
                                    value={newEnvStatusValue}
                                    onChange={(e) => setNewEnvStatusValue(e.target.value as any)}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      fontSize: '13px',
                                      border: '1px solid var(--border)',
                                      borderRadius: '6px',
                                      background: 'var(--bg3)',
                                      color: 'var(--sf-white)',
                                      outline: 'none',
                                    }}
                                  >
                                    <option value="PENDING" style={{ background: 'var(--bg3)' }}>Pendente</option>
                                    <option value="IN_PROGRESS" style={{ background: 'var(--bg3)' }}>Em andamento</option>
                                    <option value="COMPLETED" style={{ background: 'var(--bg3)' }}>Concluído</option>
                                  </select>

                                  {envStatusUpdateError && (
                                    <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--sf-red)', marginTop: '6px', marginBottom: 0, textAlign: 'left' }}>
                                      {envStatusUpdateError}
                                    </p>
                                  )}

                                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingEnvStatusId(null);
                                        setNewEnvStatusValue('');
                                        setEnvStatusUpdateError(null);
                                      }}
                                      style={{
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        background: 'transparent',
                                        color: 'var(--sf-white)',
                                        border: '1px solid var(--border)',
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isUpdatingStatus}
                                      onClick={() => handleConfirmEnvStatusChange(env.id, env.status, env.name)}
                                      style={{
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        background: 'var(--blue, #1a6bff)',
                                        color: '#fff',
                                        border: 'none',
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--blue2, #2979ff)')}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--blue, #1a6bff)')}
                                    >
                                      {isUpdatingStatus ? 'Salvando...' : 'Confirmar'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Botão Iniciar */}
                          {env.status === 'PENDING' && (currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT' || (currentUser.role === 'TECHNICIAN' && env.technicianId === currentUser.id)) && (
                            <button
                              onClick={() => handleStartEnvironment(env.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '5px 12px', borderRadius: 7,
                                background: 'rgba(56,139,253,.15)',
                                border: '1px solid rgba(56,139,253,.3)',
                                color: '#388bfd', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                              }}
                              className="flex-shrink-0"
                            >
                              ▶ Iniciar
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Sub-info list under the header */}
                      <div className="mt-3 flex flex-wrap" style={{ gap: '8px', marginBottom: '24px' }}>
                        <div className="flex items-center gap-1.5 text-xs bg-gray-50 text-gray-700 border border-gray-200 px-2.5 py-1 rounded-md shadow-sm">
                          <Calendar size={13} className="text-gray-500" />
                          <span className="font-medium">Previsão: {env.estimatedDays} dia(s)</span>
                        </div>
                        {env.technicianName && (
                          <div className="flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-md shadow-sm">
                            <UserIcon size={13} />
                            <span className="font-bold">Resp: {env.technicianName}</span>
                          </div>
                        )}
                        {env.startDate && (
                          <div className="flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-md shadow-sm">
                            <Calendar size={13} />
                            <span>Início: <span className="font-bold">{safeFormat(env.startDate)}</span></span>
                          </div>
                        )}
                        {env.deadlineDate && (
                          <div className="flex items-center gap-1.5 text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-md shadow-sm">
                            <Clock size={13} />
                            <span>Prazo: <span className="font-bold">{safeFormat(env.deadlineDate)}</span></span>
                          </div>
                        )}
                        {env.status === 'COMPLETED' && env.completedDate && (
                          <div className="flex items-center gap-1.5 text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-md shadow-sm">
                            <CheckCircle size={13} />
                            <span>Concluído: <span className="font-bold">{safeFormat(env.completedDate)}</span></span>
                          </div>
                        )}
                        {env.startDate && env.status !== 'COMPLETED' && (
                          <div className="flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-md shadow-sm">
                            <Clock size={13} />
                            <span className="font-bold">Em execução: {calculateBusinessDays(env.startDate, new Date().toISOString())} dia(s)</span>
                          </div>
                        )}
                        {env.startDate && env.status === 'COMPLETED' && env.completedDate && (
                          <div className="flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-md shadow-sm">
                            <Clock size={13} />
                            <span className="font-bold">Duração: {calculateBusinessDays(env.startDate, env.completedDate)} dia(s)</span>
                          </div>
                        )}
                        {env.value_brl !== undefined && (currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                          <div className="flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-md shadow-sm">
                            <span className="font-black">{env.value_brl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-3 border border-gray-200">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200 pb-2 mb-2">Checklist</p>
                    {!isEditing && env.checklist && (<ProgressBar items={env.checklist} />)}
                    {isEditing ? (
                      <div className="space-y-2">
                        {env.checklist.map(item => (<div key={item.id} className="flex items-center gap-3 bg-white p-2 rounded border border-gray-200 shadow-sm group"> <span className="flex-1 text-sm font-medium text-gray-900">{item.label}</span> <button type="button" onClick={() => handleRemoveEnvItem(idx, item.id)} className="text-red-400 hover:text-red-600 p-1"> <Trash size={14} /> </button> </div>))}
                        <div className="flex gap-2 pt-2 border-t border-gray-200 mt-2">
                          <input type="text" value={envNewItemTexts[idx] || ''} onChange={(e) => setEnvNewItemTexts({ ...envNewItemTexts, [idx]: e.target.value })} placeholder="Nova tarefa..." className="flex-1 p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 text-gray-900 bg-white" />
                          <button type="button" onClick={() => handleAddEnvItem(idx)} className="bg-brand-600 text-white p-2 rounded hover:bg-brand-700"> <Plus size={16} /> </button>
                        </div>
                      </div>
                    ) : (env.checklist.map(item => (<div key={item.id} className="flex items-center gap-3 bg-white p-2 rounded border border-gray-100 shadow-sm"> <button disabled={isLockedForTech || (currentUser.role !== 'TECHNICIAN' && currentUser.role !== 'ADMIN')} onClick={() => handleEnvironmentCheck(idx, item.id)} className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${(isLockedForTech || (currentUser.role !== 'TECHNICIAN' && currentUser.role !== 'ADMIN')) ? 'cursor-not-allowed opacity-70' : ''} ${item.checked ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-gray-400 hover:border-brand-500'}`}> {item.checked && <CheckSquare size={14} />} </button> <span className={`text-sm font-medium ${item.checked ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{item.label}</span> </div>)))}
                  </div>
                  {/* Project File Section — hidden from CLIENT */}
                  {currentUser.role !== 'CLIENT' && !isEditing && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                          <FileText size={13} className="text-rose-500" />
                          Projeto do Ambiente
                        </span>
                        <div className="flex items-center gap-2">
                          {/* Add / Replace Project — ADMIN and ASSISTANT only */}
                          {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                            <>
                              <input
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                ref={(el) => { projectFileInputRefs.current[idx] = el; }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    if (reader.result) {
                                      const newEnvs = [...(order.environments || [])];
                                      newEnvs[idx] = {
                                        ...newEnvs[idx],
                                        projectFileUrl: reader.result as string,
                                        projectFileName: file.name
                                      };
                                      onUpdateOrder({
                                        ...order,
                                        environments: newEnvs,
                                        updatedAt: new Date().toISOString(),
                                        history: [...order.history, {
                                          action: `Projeto adicionado ao ambiente "${env.name}": ${file.name}`,
                                          timestamp: new Date().toISOString(),
                                          user: currentUser.name
                                        }]
                                      });
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                  e.target.value = '';
                                }}
                              />
                              <button
                                onClick={() => projectFileInputRefs.current[idx]?.click()}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors"
                              >
                                <Plus size={13} />
                                {hasProjectFile ? 'Substituir' : 'Add Projeto'}
                              </button>
                            </>
                          )}
                          {/* View Project — ADMIN, ASSISTANT, TECHNICIAN */}
                          {hasProjectFile && (
                            <a
                              href={env.projectFileUrl}
                              download={env.projectFileName || 'projeto.pdf'}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100 transition-colors"
                            >
                              <FileText size={13} />
                              Ver Projeto
                            </a>
                          )}
                        </div>
                      </div>
                      {hasProjectFile ? (
                        <div className="mt-2 flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                          <FileText size={16} className="text-rose-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-rose-900 truncate">{env.projectFileName || 'projeto.pdf'}</p>
                            <p className="text-[10px] text-rose-400">PDF • Clique em "Ver Projeto" para baixar</p>
                          </div>
                          {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                            <button
                              onClick={() => {
                                const newEnvs = [...(order.environments || [])];
                                newEnvs[idx] = { ...newEnvs[idx], projectFileUrl: undefined, projectFileName: undefined };
                                onUpdateOrder({ ...order, environments: newEnvs, updatedAt: new Date().toISOString() });
                              }}
                              className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-100 rounded-full transition-colors flex-shrink-0"
                              title="Remover projeto"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-400 italic">
                          {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT')
                            ? 'Nenhum projeto adicionado. Clique em "Add Projeto" para anexar o PDF.'
                            : 'Nenhum projeto disponível para este ambiente.'}
                        </p>
                      )}
                    </div>
                  )}

                  {!isEditing && env.status !== 'COMPLETED' && !isLockedForTech && (currentUser.role === 'TECHNICIAN' || currentUser.role === 'ADMIN') && (
                    <button 
                      onClick={() => handleEnvironmentFinish(idx)} 
                      disabled={!(env.checklist?.length === 0 || env.checklist?.every(item => item.checked))}
                      className={`w-full py-2 border-2 rounded-lg font-medium transition-colors mt-3 ${(!env.checklist || env.checklist.length === 0 || env.checklist.every(item => item.checked)) ? 'border-green-600 text-green-700 hover:bg-green-50' : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed opacity-70'}`}
                      title={(!env.checklist || env.checklist.length === 0 || env.checklist.every(item => item.checked)) ? '' : 'Conclua o checklist antes de finalizar o ambiente.'}
                    > 
                      Marcar Ambiente como Finalizado 
                    </button>
                  )}
                  {!isEditing && (currentUser.role === 'ADMIN' || currentUser.role === 'TECHNICIAN') && env.status === 'COMPLETED' && env.completedDate && env.deadlineDate && (
                    <div className={`text-center text-sm font-bold p-2.5 rounded shadow-sm border mt-2 ${
                        (env.startDate ? calculateBusinessDays(env.startDate, env.completedDate) <= (env.estimatedDays || 0) : new Date(env.completedDate.substring(0,10)) <= new Date(env.deadlineDate.substring(0,10)))
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200' 
                        : 'text-red-700 bg-red-50 border-red-200'
                    }`}> 
                      {(env.startDate ? calculateBusinessDays(env.startDate, env.completedDate) <= (env.estimatedDays || 0) : new Date(env.completedDate.substring(0,10)) <= new Date(env.deadlineDate.substring(0,10))) ? 'ENTREGUE NO PRAZO' : `ENTREGUE COM ATRASO (${calculateBusinessDays(env.deadlineDate, env.completedDate) - 1} dias úteis)`} 
                    </div>
                  )}
                </div>
              );
            })}

            {/* Adicionar Novo Ambiente Builder */}
            {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
              <div className="p-4 rounded-xl mt-6 w-full" style={{ background: 'rgba(251,146,60,.07)', border: '1px solid rgba(251,146,60,.2)' }}>
                <h4 className="font-semibold mb-3 pb-2" style={{ color: '#fb923c', borderBottom: '1px solid rgba(251,146,60,.2)' }}>Adicionar Ambiente e Serviços</h4>

                <div className="space-y-3 mb-4">
                  <div className="flex flex-col md:flex-row gap-2">
                    <input
                      placeholder="Nome do Ambiente (ex: Cozinha)"
                      value={newEnvName}
                      onChange={e => setNewEnvName(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900"
                    />
                    <div className="flex gap-2">
                      {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                        <input
                          type="tel"
                          inputMode="numeric"
                          placeholder="R$ 0,00"
                          value={newEnvValueBrl}
                          onChange={(e) => {
                            let val = e.target.value.replace(/\D/g, '');
                            if (!val) {
                              setNewEnvValueBrl('');
                              return;
                            }
                            const num = Number(val) / 100;
                            setNewEnvValueBrl(num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
                          }}
                          className="w-32 p-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900"
                        />
                      )}
                      <div className="relative w-24">
                        <input
                          type="number"
                          min="1"
                          value={newEnvDays}
                          onChange={e => setNewEnvDays(parseInt(e.target.value) || 1)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900"
                          title="Dias Úteis"
                        />
                        <span className="absolute right-2 top-2 text-[10px] text-gray-500">dias</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <DatePicker
                      label="Data de Início do Ambiente"
                      value={newEnvStartDate}
                      onChange={setNewEnvStartDate}
                    />

                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                        <UserIcon size={12} /> Técnico (Opcional)
                      </label>
                      <div className="relative">
                        <select
                          value={newEnvTechnicianId}
                          onChange={e => setNewEnvTechnicianId(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-500 outline-none text-gray-900 appearance-none cursor-pointer"
                        >
                          <option value="">Selecione um técnico...</option>
                          {allTechnicians.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(251,146,60,.25)' }}>
                    <label className="text-xs font-bold mb-1 flex items-center gap-1" style={{ color: '#fb923c' }}>
                      <ListPlus size={14} /> Tarefas / Sub-tópicos do Ambiente
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        placeholder="Ex: Montar armários aéreos"
                        value={newEnvSubItemInput}
                        onChange={e => setNewEnvSubItemInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddEnvSubItem(e)}
                        className="flex-1 p-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900"
                      />
                      <Button
                        type="button"
                        onClick={handleAddEnvSubItem}
                        className="bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-200 shadow-none h-10 px-4"
                      >
                        Add
                      </Button>
                    </div>

                    {newEnvSubItems.length > 0 && (
                      <ul className="space-y-1 mb-2">
                        {newEnvSubItems.map((item, idx) => (
                          <li key={idx} className="flex justify-between items-center bg-white px-2 py-1 rounded border border-gray-100 shadow-sm">
                            <span className="text-xs text-gray-700">• {item}</span>
                            <button type="button" onClick={() => removeEnvSubItem(idx)} className="text-red-400 hover:text-red-600"><Trash size={12} /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="p-3 rounded-lg" style={{ background: 'rgba(244,63,94,.06)', border: '1px solid rgba(244,63,94,.2)' }}>
                    <label className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: '#fb7185' }}>
                      <FileText size={14} /> Projeto do Ambiente (PDF)
                    </label>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      ref={newEnvProjectFileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          if (reader.result) {
                            setNewEnvProjectFile({ url: reader.result as string, name: file.name });
                          }
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    {newEnvProjectFile ? (
                      <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                        <FileText size={16} className="text-rose-500 flex-shrink-0" />
                        <span className="text-xs font-semibold text-rose-900 truncate flex-1">{newEnvProjectFile.name}</span>
                        <button
                          type="button"
                          onClick={() => { setNewEnvProjectFile(null); if (newEnvProjectFileInputRef.current) newEnvProjectFileInputRef.current.value = ''; }}
                          className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-100 rounded-full transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => newEnvProjectFileInputRef.current?.click()}
                        className="flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors w-full justify-center"
                      >
                        <Plus size={14} /> Add Projeto
                      </button>
                    )}
                  </div>

                  <Button
                    type="button"
                    onClick={handleAddEnvironment}
                    disabled={!newEnvName}
                    fullWidth
                    className="bg-orange-600 hover:bg-orange-700 text-white border-transparent"
                    icon={<Plus size={18} />}
                  >
                    Adicionar Ambiente à O.S.
                  </Button>
                </div>
              </div>
            )}

          </div>
        )}
        {activeTab === 'CHAT' && (
          <div className="h-[calc(100vh-260px)] min-h-[400px] flex flex-col max-w-2xl mx-auto rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)' }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {order.comments.length === 0 && (<p className="text-center text-gray-400 text-sm mt-10">Nenhuma mensagem ainda.</p>)}
              {order.comments.map(comment => {
                const isMe = comment.userId === currentUser.id;
                return (
                  <div key={comment.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl p-3 ${isMe ? 'bg-brand-600 text-white rounded-br-none' : 'bg-gray-100 text-gray-900 rounded-bl-none'}`}>
                      <p className={`text-xs font-bold mb-1 opacity-80 ${isMe ? 'text-white' : 'text-gray-600'}`}>{comment.userName}</p>
                      <p className="text-sm">{comment.text}</p>
                      {comment.attachmentUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-white/20">
                          {comment.attachmentType === 'video' && (
                            <video src={comment.attachmentUrl} controls className="w-full h-auto rounded" />
                          )}
                          {comment.attachmentType === 'image' && (
                            <img src={comment.attachmentUrl} alt="anexo" className="w-full h-auto object-cover rounded" />
                          )}
                          {comment.attachmentType === 'audio' && (
                            <div className={`flex items-center gap-3 p-2.5 rounded-lg ${isMe ? 'bg-brand-700/40' : 'bg-white'}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isMe ? 'bg-white/20' : 'bg-brand-100'}`}>
                                <Mic size={14} className={isMe ? 'text-white' : 'text-brand-600'} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <audio src={comment.attachmentUrl} controls className="w-full h-8" style={{ filter: isMe ? 'invert(1) brightness(2)' : 'none' }} />
                              </div>
                              {comment.audioDuration && (
                                <span className={`text-[10px] font-mono flex-shrink-0 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>
                                  {formatRecordingTime(comment.audioDuration)}
                                </span>
                              )}
                            </div>
                          )}
                          {comment.attachmentType === 'document' && (() => {
                            const docInfo = getDocIcon(comment.attachmentName, comment.attachmentMimeType);
                            const DocIcon = docInfo.icon;
                            return (
                              <a
                                href={comment.attachmentUrl}
                                download={comment.attachmentName || 'arquivo'}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isMe ? 'bg-brand-700/40 hover:bg-brand-700/60' : 'bg-white hover:bg-gray-50 border border-gray-200'}`}
                              >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isMe ? 'bg-white/20' : docInfo.bg}`}>
                                  <DocIcon size={20} className={isMe ? 'text-white' : docInfo.color} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${isMe ? 'text-white' : 'text-gray-900'}`}>
                                    {comment.attachmentName || 'Documento'}
                                  </p>
                                  <p className={`text-[10px] ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                                    {docInfo.label} • Clique para baixar
                                  </p>
                                </div>
                                <Paperclip size={14} className={isMe ? 'text-white/50' : 'text-gray-300'} />
                              </a>
                            );
                          })()}
                        </div>
                      )}
                      <p className="text-[10px] text-right mt-1 opacity-70">{safeFormat(comment.timestamp, 'HH:mm')}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3" style={{ background: 'rgba(255,255,255,.04)', borderTop: '1px solid rgba(255,255,255,.08)' }}>
              {isProcessingFile && (<div className="flex items-center gap-2 mb-2 p-2 bg-brand-50 border border-brand-100 rounded-lg"> <Loader2 size={16} className="animate-spin text-brand-600" /> <span className="text-xs font-bold text-brand-800 uppercase">Processando Arquivo...</span> </div>)}
              {attachment && (
                <div className="flex gap-2 mb-2 p-2 bg-white rounded-lg border w-fit relative">
                  {attachment.type === 'image' && (
                    <img src={attachment.preview} alt="preview" className="h-20 w-auto rounded object-cover" />
                  )}
                  {attachment.type === 'video' && (
                    <video src={attachment.preview} className="h-20 w-auto rounded bg-black" />
                  )}
                  {attachment.type === 'audio' && (
                    <div className="flex items-center gap-2 px-2">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <Mic size={14} className="text-brand-600" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700">Áudio gravado</span>
                        <span className="text-[10px] text-gray-400">{attachment.duration ? formatRecordingTime(attachment.duration) : '00:00'}</span>
                      </div>
                      <audio src={attachment.preview} controls className="h-8" />
                    </div>
                  )}
                  {attachment.type === 'document' && (() => {
                    const docInfo = getDocIcon(attachment.name, attachment.mimeType);
                    const DocIcon = docInfo.icon;
                    return (
                      <div className="flex items-center gap-2 px-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${docInfo.bg}`}>
                          <DocIcon size={20} className={docInfo.color} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-700 max-w-[200px] truncate">{attachment.name || 'Documento'}</span>
                          <span className="text-[10px] text-gray-400">{docInfo.label}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <button onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ''; if (mediaFileInputRef.current) mediaFileInputRef.current.value = ''; }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow-md hover:bg-red-600 transition-colors"><X size={12} /></button>
                </div>
              )}

              {/* Recording UI */}
              {isLockedForTech ? (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg text-orange-800 text-sm font-medium text-center">
                   A O.S. já foi assinada pelo cliente. O chat está bloqueado para novas interações.
                </div>
              ) : isRecording ? (
                <div className="flex gap-2 items-center bg-red-50 border border-red-200 rounded-full px-3 py-2 animate-pulse">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-ping" />
                  <span className="text-sm font-bold text-red-700 font-mono min-w-[48px]">{formatRecordingTime(recordingTime)}</span>
                  <div className="flex-1 flex items-center justify-center gap-[3px] mx-2">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-[3px] bg-red-400 rounded-full"
                        style={{
                          height: `${Math.random() * 16 + 4}px`,
                          animation: `pulse ${0.3 + Math.random() * 0.5}s ease-in-out infinite alternate`,
                          animationDelay: `${i * 0.05}s`
                        }}
                      />
                    ))}
                  </div>
                  <button onClick={cancelRecording} className="px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-100 rounded-full transition-colors">Cancelar</button>
                  <button onClick={stopRecording} className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-sm flex items-center justify-center">
                    <Square size={16} fill="white" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  {/* Hidden file inputs */}
                  <input type="file" ref={fileInputRef} className="hidden" accept="*/*" onChange={handleFileSelect} />
                  <input type="file" ref={mediaFileInputRef} className="hidden" accept="image/*,video/*" onChange={handleFileSelect} />

                  {/* Paperclip: all files */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                    title="Anexar arquivo (PDF, DOC, etc.)"
                  >
                    <Paperclip size={20} />
                  </button>

                  {/* Camera: media only */}
                  <button
                    onClick={() => mediaFileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                    title="Enviar foto ou vídeo"
                  >
                    <Camera size={20} />
                  </button>

                  {/* Mic: audio recording */}
                  <button
                    onClick={startRecording}
                    className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
                    title="Gravar áudio"
                  >
                    <Mic size={20} />
                  </button>

                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 p-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm bg-white text-gray-900"
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button onClick={handleSendMessage} className="p-2 bg-brand-600 text-white rounded-full hover:bg-brand-700 transition-colors shadow-sm">
                    <Send size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'OCCURRENCES' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
               <div>
                  <h2 className="text-lg font-bold text-gray-900">Histórico de Ocorrências</h2>
                  <p className="text-sm text-gray-500">Acompanhamento e bloqueios da ordem de serviço.</p>
               </div>
               {!isLockedForTech && (
                 <Button onClick={() => setShowOccurrenceModal(true)} className="bg-brand-600 shadow-md shadow-brand-500/20" icon={<Plus size={18}/>}>
                    Nova Ocorrência
                 </Button>
               )}
            </div>

            {isLoadingEvents ? (
               <div className="flex items-center justify-center py-10 text-gray-500">
                  <Loader2 className="animate-spin mr-2" size={24} /> Carregando...
               </div>
            ) : events.length === 0 ? (
               <div className="rounded-xl p-8 text-center" style={{ border: '1px dashed rgba(255,255,255,.15)', background: 'rgba(255,255,255,.02)' }}>
                  <AlertCircle size={40} className="mx-auto mb-3" style={{ color: 'var(--hint)' }} />
                  <p style={{ color: 'var(--muted)' }} className="font-medium">Nenhuma ocorrência registrada.</p>
               </div>
            ) : (
               <div className="relative border-l-2 border-brand-100 ml-3 space-y-6 pb-4">
                  {events.map((evt) => (
                    <div key={evt.id} className="relative pl-6">
                      <div className={`absolute -left-[11px] top-1 w-5 h-5 rounded-full border-4 flex items-center justify-center shadow-sm ${
                        evt.status === 'resolved' ? 'bg-green-500' :
                        ['Atualização de andamento', 'Cliente orientado/alinhamento', 'Mudança solicitada pelo cliente', 'Ajuste extra realizado', 'Evidência de entrega', 'Observação técnica', 'Elogio/boa prática', 'Outros'].includes(evt.category) || evt.category === 'INFO' ? 'bg-blue-500' :
                        ['Falta de material/peça', 'Peça errada', 'Peça avariada', 'Material insuficiente', 'Ferramenta essencial indisponível', 'Local não liberado pelo cliente', 'Cliente ausente/sem acesso', 'Impedimento físico no ambiente', 'Infraestrutura inadequada (parede/piso/estrutura)', 'Ponto de energia/água não disponível', 'Medidas divergentes/projeto inviável', 'Necessário retrabalho prévio', 'Aguardando aprovação', 'Aguardando orientação técnica', 'Aguardando entrega logística', 'Aguardando agendamento/reagendamento'].includes(evt.category) || evt.category === 'BLOCKER' ? 'bg-orange-500' : 'bg-red-500'
                      }`}></div>
                      
                      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:border-brand-200 hover:shadow-md transition-all">
                         <div className="flex justify-between items-start mb-2">
                            <div>
                               <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full tracking-wider ${
                                     ['Atualização de andamento', 'Cliente orientado/alinhamento', 'Mudança solicitada pelo cliente', 'Ajuste extra realizado', 'Evidência de entrega', 'Observação técnica', 'Elogio/boa prática', 'Outros'].includes(evt.category) || evt.category === 'INFO' ? 'bg-blue-50 text-blue-700' :
                                     ['Falta de material/peça', 'Peça errada', 'Peça avariada', 'Material insuficiente', 'Ferramenta essencial indisponível', 'Local não liberado pelo cliente', 'Cliente ausente/sem acesso', 'Impedimento físico no ambiente', 'Infraestrutura inadequada (parede/piso/estrutura)', 'Ponto de energia/água não disponível', 'Medidas divergentes/projeto inviável', 'Necessário retrabalho prévio', 'Aguardando aprovação', 'Aguardando orientação técnica', 'Aguardando entrega logística', 'Aguardando agendamento/reagendamento'].includes(evt.category) || evt.category === 'BLOCKER' ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'
                                  }`}>
                                     {evt.category}
                                  </span>
                                  <span className="text-xs text-gray-500 font-medium">
                                     {evt.createdAt && !isNaN(new Date(evt.createdAt).getTime()) 
                                        ? format(new Date(evt.createdAt), 'dd/MM HH:mm') 
                                        : 'Data não disponível'}
                                  </span>
                                  {evt.status === 'open' && <span className="text-[10px] border border-gray-300 text-gray-600 bg-gray-50 px-2 rounded font-bold uppercase">Em Aberto</span>}
                                  {evt.status === 'resolved' && <span className="text-[10px] border border-green-300 text-green-700 bg-green-50 px-2 rounded font-bold uppercase flex items-center gap-1"><CheckCircle size={10}/> Resolvido</span>}
                               </div>
                               <h3 className="font-bold text-gray-900">{evt.title}</h3>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                               {evt.visibility === 'client_shareable' ? (
                                  <span className="text-[10px] border border-green-200 text-green-700 bg-green-50 px-1.5 py-0.5 rounded flex items-center gap-1" title="Visível para o cliente"><UserCheck size={10}/> Compartilhado</span>
                               ) : (
                                  <span className="text-[10px] border border-gray-200 text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded flex items-center gap-1" title="Somente equipe interna"><Lock size={10}/> Interno</span>
                               )}
                               {evt.environmentName && (
                                  <span className="text-[10px] text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded font-medium border border-brand-100 flex items-center gap-1"><MapPin size={10}/> {evt.environmentName}</span>
                               )}
                               {(currentUser.role === 'ADMIN' || currentUser.role === 'ASSISTANT') && (
                                 <div className="flex items-center gap-1 mt-1 bg-gray-100 p-0.5 rounded border border-gray-200">
                                    <button onClick={() => { setEditEventData(evt); setShowOccurrenceModal(true); }} className="p-1 hover:bg-blue-100 text-blue-600 rounded" title="Editar ocorrência">
                                       <Pencil size={12}/>
                                    </button>
                                    <button onClick={() => handleDeleteEvent(evt.id, evt.title)} className="p-1 hover:bg-red-100 text-red-600 rounded" title="Excluir ocorrência">
                                       <Trash size={12}/>
                                    </button>
                                 </div>
                               )}
                            </div>
                         </div>
                           
                           {evt.description && <p className="text-sm text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100 mt-2">{evt.description}</p>}
                           
                           {(evt.impactDeadline || evt.impactCost) && (
                              <div className="flex gap-2 mt-3 pt-3 border-t border-dashed border-gray-200">
                                 {evt.impactDeadline && (
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-orange-700 bg-orange-50 px-2.5 py-1.5 rounded-md">
                                       <Clock size={14}/> +{evt.impactWorkdays} dia(s) útil(eis)
                                    </div>
                                 )}
                                 {evt.impactCost && (
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 px-2.5 py-1.5 rounded-md">
                                       Gastos: {(evt.impactCostBrl || 0).toLocaleString('pt-BR', {style: 'currency', currency:'BRL'})}
                                    </div>
                                 )}
                              </div>
                           )}

                           {evt.attachments && evt.attachments.length > 0 && (
                              <div className="flex gap-2 mt-3 flex-wrap">
                                 {evt.attachments.map((att: any) => (
                                    <button 
                                      key={att.id} 
                                      type="button"
                                      onClick={() => setEventMediaModalData({ attachments: evt.attachments, initialIndex: evt.attachments.findIndex((a: any) => a.id === att.id) })} 
                                      className="flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 hover:border-brand-300 text-gray-700 px-2.5 py-1.5 rounded-md border border-gray-200 transition-colors cursor-pointer text-left"
                                    >
                                       {att.fileType?.startsWith('image/') ? <Camera size={14}/> : <Paperclip size={14}/>}
                                       <span className="max-w-[120px] truncate">{att.fileName}</span>
                                    </button>
                                 ))}
                              </div>
                           )}
                           
                           <div className="mt-3 text-xs text-gray-400 flex items-center justify-between">
                              <span>Registrado por: <strong>{evt.createdByName}</strong></span>
                              {evt.status === 'open' && (currentUser.id === evt.createdBy || currentUser.role === 'ADMIN') && (
                                 <button 
                                    onClick={() => {
                                       setConfirmModal({
                                          title: 'Resolver Ocorrência',
                                          message: 'Marcar ocorrência como resolvida?',
                                          confirmLabel: 'Confirmar',
                                          cancelLabel: 'Cancelar',
                                          onConfirm: async () => {
                                             setConfirmModal(null);
                                             await db.updateWorkOrderEventStatus(evt.id, 'resolved');
                                             loadEvents();
                                          },
                                          onCancel: () => setConfirmModal(null)
                                       });
                                    }} 
                                    className="text-brand-600 hover:text-brand-800 font-bold"
                                 >
                                    Marcar Resolvido
                                 </button>
                              )}
                           </div>
                        </div>
                      </div>
                    ))}
                 </div>
              )}

              {showOccurrenceModal && (
                 <OccurrenceFormModal 
                    currentUser={currentUser} 
                    workOrder={order} 
                    initialData={editEventData}
                    onClose={() => { setShowOccurrenceModal(false); setEditEventData(null); }}
                    onSave={async (payload, files) => {
                       await handleSaveEvent(payload, files);
                       setShowOccurrenceModal(false);
                       setEditEventData(null);
                    }}
                    onEnvironmentCreated={(newEnv) => {
                       const updatedEnvs = [...(order.environments || []), newEnv];
                       onUpdateOrder({
                          ...order,
                          environments: updatedEnvs,
                          updatedAt: new Date().toISOString()
                       });
                    }}
                 />
              )}
              
              {eventMediaModalData && (
                <EventMediaModal 
                  attachments={eventMediaModalData.attachments} 
                  initialIndex={eventMediaModalData.initialIndex}
                  onClose={() => setEventMediaModalData(null)}
                />
              )}
            </div>
          )}

        {activeTab === 'CHECKLIST' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            <ChecklistErrorBoundary>
              <OSChecklist osId={order.id} userRole={currentUser.role} />
            </ChecklistErrorBoundary>
          </div>
        )}
      </div>
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.05)' }}>
            
            {/* Background glowing effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-brand-500/10 blur-[50px] rounded-full pointer-events-none" />

            <div className="relative z-10">
               <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full" style={{ background: 'rgba(26, 107, 255, 0.1)', border: '1px solid rgba(26, 107, 255, 0.2)' }}>
                     <Lock size={32} className="text-brand-500" />
                  </div>
               </div>
               
               <h3 className="font-bold text-xl text-center text-white mb-2 tracking-wide">Assinatura Digital</h3>
               <p className="text-sm text-center mb-8" style={{ color: 'var(--muted)' }}>
                  Para confirmar a conclusão do serviço com segurança, digite sua senha de acesso abaixo.
               </p>
               
               <div className="space-y-6">
                 <div> 
                    <label className="block text-xs font-bold uppercase mb-2" style={{ color: 'var(--hint)' }}>Sua Senha</label> 
                    <div className="relative">
                       <input 
                         type="password" 
                         autoFocus 
                         className={`w-full p-3.5 pl-4 pr-10 rounded-xl outline-none transition-all ${signatureError ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : 'focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'}`} 
                         style={{ 
                            background: 'rgba(0,0,0,0.2)', 
                            border: `1px solid ${signatureError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`, 
                            color: 'var(--sf-white)' 
                         }}
                         placeholder="••••••" 
                         value={signaturePassword} 
                         onChange={(e) => { setSignaturePassword(e.target.value); setSignatureError(''); }} 
                       /> 
                       {signatureError && <p className="text-xs text-red-400 mt-2 flex items-center gap-1"><AlertTriangle size={12}/> {signatureError}</p>}
                    </div>
                 </div>
                 
                 <div className="flex gap-3 pt-2"> 
                    <button onClick={() => { setShowSignatureModal(false); setSignaturePassword(''); setSignatureError(''); }} className="flex-1 py-3.5 rounded-xl font-bold transition-colors" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--sf-white)', border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button> 
                    <button onClick={handleSignatureSubmit} disabled={!signaturePassword} className="flex-1 py-3.5 bg-brand-600 text-white rounded-xl font-bold hover:bg-brand-500 disabled:opacity-50 transition-colors shadow-[0_0_20px_rgba(26,107,255,0.3)]">Aprovar e Concluir</button> 
                 </div>
               </div>
            </div>
          </div>
        </div>
      )}
      {showRatingPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden text-center" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-green-500/10 blur-[50px] rounded-full pointer-events-none" />
            <div className="relative z-10">
               <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                     <CheckCircle size={32} className="text-green-500" />
                  </div>
               </div>
               <h3 className="font-bold text-xl text-white mb-2 tracking-wide">Serviço Concluído!</h3>
               <p className="text-sm mb-8" style={{ color: 'var(--muted)' }}>Gostaria de avaliar o atendimento do técnico agora? Sua opinião é muito importante para nós.</p>
               <div className="flex gap-3"> 
                  <button onClick={() => setShowRatingPrompt(false)} className="flex-1 py-3.5 rounded-xl font-bold transition-colors" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--sf-white)', border: '1px solid rgba(255,255,255,0.1)' }}>Mais tarde</button> 
                  <button onClick={handleGoToRating} className="flex-1 py-3.5 bg-yellow-500 text-yellow-950 rounded-xl font-bold hover:bg-yellow-400 transition-colors shadow-[0_0_20px_rgba(234,179,8,0.2)]">Avaliar Agora</button> 
               </div>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-red-500/10 blur-[50px] rounded-full pointer-events-none" />
            <div className="relative z-10">
               <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-full" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                     <XCircle size={24} className="text-red-500" />
                  </div>
                  <h3 className="font-bold text-xl text-white tracking-wide">Cancelar Ordem</h3>
               </div>
               <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>Por favor, informe o motivo do cancelamento:</p>
               <textarea 
                  className="w-full p-3.5 rounded-xl outline-none transition-all focus:border-red-500 focus:ring-2 focus:ring-red-500/20 mb-6 resize-none" 
                  style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--sf-white)' }} 
                  rows={4} 
                  value={cancelReason} 
                  onChange={(e) => setCancelReason(e.target.value)} 
                  placeholder="Descreva o motivo detalhadamente..." 
               />
               <div className="flex gap-3"> 
                  <button onClick={() => setShowCancelModal(false)} className="flex-1 py-3.5 rounded-xl font-bold transition-colors" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--sf-white)', border: '1px solid rgba(255,255,255,0.1)' }}>Voltar</button> 
                  <button onClick={handleCancel} disabled={!cancelReason.trim()} className="flex-1 py-3.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-500 disabled:opacity-50 transition-colors shadow-[0_0_20px_rgba(239,68,68,0.3)]">Confirmar Cancelamento</button> 
               </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-red-500/10 blur-[50px] rounded-full pointer-events-none" />
            <div className="relative z-10 text-center">
               <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                     <Trash size={32} className="text-red-500" />
                  </div>
               </div>
               <h3 className="font-bold text-xl text-white mb-3 tracking-wide">Excluir Permanentemente</h3>
               <p className="text-sm mb-8" style={{ color: 'var(--muted)' }}>
                  Tem certeza? Esta ação removerá a ordem <strong className="text-white">{order.id}</strong> do sistema e não poderá ser desfeita. Considere usar "Cancelar" se quiser manter o histórico.
               </p>
               <div className="flex gap-3"> 
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-3.5 rounded-xl font-bold transition-colors" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--sf-white)', border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button> 
                  <button onClick={handleDelete} className="flex-1 py-3.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-500 transition-colors shadow-[0_0_20px_rgba(239,68,68,0.3)]">Excluir Definitivo</button> 
               </div>
            </div>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
};